import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createRideAudioController } from '../audio/index.ts';
import { PointerEditor } from '../editor/pointer-editor.ts';
import {
  EditorSession,
  ValidationCoordinator,
  buildPresetCandidate,
  buildValidationProfile,
  assessFinishToStation,
  connectEndpoints,
  deleteNode,
  duplicateSection,
  measureFinishToStation,
  projectPlotBounds,
  insertNodeAtSection,
  instantiateTemplate,
  moveNodes,
  renameProject as renameProjectOperation,
  setNodeBank,
  setNodeElevation,
  setSectionBehavior as setSectionBehaviorOperation,
  smoothNodes,
  AUTO_BANK_GLOBAL_TOLERANCE_G,
  AUTO_BANK_MINIMUM_IMPROVEMENT_G,
  generateAutoBankCandidates,
  peakLateralGInIntervals,
  alignNodes,
  rotateNodes,
  removeNodeRollWinding,
  validateTrackTopology,
  newStableId,
  type EditorSelectionState,
  type EditorMode,
  type EditorOverlay,
  type EditorTool,
  type ElementPresetId,
  type ValidationProfile,
} from '../editor/index.ts';
import {
  blankCoasterFixture,
  extremeWarningCoasterFixture,
  liftDropCoasterFixture,
  ovalCoasterFixture,
  stallHillCoasterFixture,
  systemsCircuitCoasterFixture,
} from '../fixtures/index.ts';
import {
  FIXED_PHYSICS_STEP,
  createTrackBoundState,
  type SimulationFrame,
  type TrackBoundState,
} from '../physics/index.ts';
import { createNamedProjectRepository, type StoredDraftSummary } from '../persistence/index.ts';
import { TrainVisual, TrackVisual } from '../ride/track-visual.ts';
import { RideCameraController } from '../ride/camera-controller.ts';
import { RideRunner, type RideSummary } from '../ride/runner.ts';
import {
  BunchaSceneService,
  createCoasterMaterials,
  disposeMaterialSet,
  recommendQualityTier,
} from '../scene/index.ts';
import type { EnvironmentThemeId } from '../scene/index.ts';
import {
  loadCoasterProject,
  serializeCoasterProject,
  type CoasterProject,
  type TrackBehavior,
  type TrackNode,
} from '../schema/index.ts';
import { TrackCompiler, type TableTrackSampler } from '../track/index.ts';
import type { SemanticSectionType } from '../track/index.ts';
import {
  FullRunValidationCoordinator,
  toAuthoritativeValidationSummary,
  type FullRunValidationResult,
} from '../validation/index.ts';
import { transitionMode, type AppMode } from './mode.ts';
import {
  coasterFamilyForProject,
  compileProjectTrackResult,
  initialTrainReferenceDistance,
  type CompiledProjectTrack,
} from './ride-track.ts';
import {
  classifySimulatedIntensity,
  simulatedIntensityScore,
  type SimulatedIntensityLevel,
  type SimulatedIntensitySummary,
} from './ride-intensity.ts';
import type {
  CoasterRuntime,
  RuntimeDiagnostics,
  RuntimeIntensity,
  RuntimeRideResult,
  RuntimeSnapshot,
  RuntimeTelemetry,
  RuntimePreviewState,
  TestSpeed,
} from './runtime-types.ts';

const MPH_PER_MPS = 2.2369362921;
const EDITOR_CAMERA_FOV = 42;
const AUTOSAVE_DELAY_MS = 750;

interface ProjectPreview {
  readonly kind: RuntimePreviewState['kind'];
  readonly presetId: ElementPresetId | null;
  readonly label: string;
  readonly command: string;
  readonly acceptLabel: string;
  readonly project: CoasterProject;
  readonly message: string;
  readonly severity: RuntimePreviewState['severity'];
  readonly canAccept: boolean;
  readonly inline: boolean;
  readonly issues: RuntimePreviewState['issues'];
  readonly affectedNodeIds: readonly string[];
  readonly affectedSectionIds: readonly string[];
  readonly minPlotMarginM: number | null;
  readonly finishImpact?: RuntimePreviewState['finishImpact'];
  readonly generatedLengthM?: number | null;
  readonly addedNodeCount?: number | null;
  readonly autoBankComparison?: RuntimePreviewState['autoBankComparison'];
  readonly focusNodeId?: string;
}

const INITIAL_INTENSITY: RuntimeIntensity = {
  ...classifySimulatedIntensity({ vertical: 1, lateral: 0, longitudinal: 0 }),
  progressPercent: 0,
};

function fixtureFor(key: 'systems' | 'stall' | 'extreme'): CoasterProject {
  return key === 'stall'
    ? stallHillCoasterFixture
    : key === 'extreme'
      ? extremeWarningCoasterFixture
      : systemsCircuitCoasterFixture;
}

function sectionLabel(type: SemanticSectionType | null): string {
  switch (type) {
    case 'station':
      return 'Station';
    case 'lift':
      return 'Lift';
    case 'launch':
      return 'Launch';
    case 'brake':
      return 'Brake';
    case 'normal':
      return 'Coasting';
    default:
      return 'Coasting';
  }
}

function cloneProject(project: CoasterProject): CoasterProject {
  const loaded = loadCoasterProject(structuredClone(project));
  if (!loaded.ok) throw new Error(loaded.error.message);
  return loaded.project;
}

function nodeSelection(
  nodeIds: readonly string[],
  primaryNodeId = nodeIds.at(-1) ?? null,
): EditorSelectionState {
  return {
    nodeIds: [...nodeIds],
    primaryNodeId,
    sectionId: null,
    sceneryIds: [],
    primarySceneryId: null,
  };
}

function sectionSelection(sectionId: string | null): EditorSelectionState {
  return {
    nodeIds: [],
    primaryNodeId: null,
    sectionId,
    sceneryIds: [],
    primarySceneryId: null,
  };
}

function percentile(values: readonly number[], fraction: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * fraction))] ?? 0;
}

function asColor(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

function finishEligible(guidance: ReturnType<typeof measureFinishToStation>): boolean {
  return guidance.directJoinEligible || guidance.generatedFinishEligible;
}

function finishImpact(
  before: CoasterProject,
  after: CoasterProject,
): RuntimePreviewState['finishImpact'] {
  if (before.track.closed || after.track.closed || before.track.nodes.length < 2) return null;
  const beforeGuidance = measureFinishToStation(before);
  const afterGuidance = measureFinishToStation(after);
  return {
    distanceBeforeM: beforeGuidance.distanceM,
    distanceAfterM: afterGuidance.distanceM,
    headingBeforeDegrees: beforeGuidance.turnDegrees,
    headingAfterDegrees: afterGuidance.turnDegrees,
    finishEligibleBefore: finishEligible(beforeGuidance),
    finishEligibleAfter: finishEligible(afterGuidance),
  };
}

function compiledTrackLength(project: CoasterProject): number | null {
  if (project.track.nodes.length < 4) return null;
  try {
    return compileProjectTrackResult(project, new TrackCompiler()).sampler.length;
  } catch {
    return null;
  }
}

function qualityTier(): 'low' | 'medium' | 'high' {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  return recommendQualityTier({
    mobile: matchMedia('(pointer: coarse)').matches || innerWidth < 720,
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    deviceMemoryGb: navigatorWithMemory.deviceMemory,
  });
}

class ProductionCoasterRuntime implements CoasterRuntime {
  private readonly listeners = new Set<(snapshot: RuntimeSnapshot) => void>();
  private readonly scene: BunchaSceneService;
  private readonly compiler = new TrackCompiler();
  private readonly audio = createRideAudioController();
  private readonly repository = createNamedProjectRepository();
  private readonly validationCoordinator = new ValidationCoordinator();
  private readonly fullValidationCoordinator = new FullRunValidationCoordinator();
  private readonly autoBankValidationCoordinator = new FullRunValidationCoordinator();
  private readonly session = new EditorSession(cloneProject(systemsCircuitCoasterFixture));
  private readonly controls: OrbitControls;
  private readonly content = new THREE.Group();
  private readonly handleRoot = new THREE.Group();
  private readonly debugRoot = new THREE.Group();
  private readonly authoredSceneryRoot = new THREE.Group();
  private readonly validationRoot = new THREE.Group();
  private readonly buildGuideRoot = new THREE.Group();
  private readonly coasterMaterials;
  private readonly ghostMaterials;
  private readonly pointerEditor: PointerEditor;
  private readonly resizeObserver: ResizeObserver;
  private activeFixture: 'systems' | 'stall' | 'extreme' = 'systems';
  private resetProject = cloneProject(systemsCircuitCoasterFixture);
  private lastValidProject = cloneProject(systemsCircuitCoasterFixture);
  private compiledTrack: CompiledProjectTrack | null = null;
  private sampler: TableTrackSampler | null = null;
  private trackVisual: TrackVisual | null = null;
  private presetPreviewVisual: TrackVisual | null = null;
  private trainVisual!: TrainVisual;
  private cameraController!: RideCameraController;
  private rideRunner: RideRunner | null = null;
  private lastRideSummary: RideSummary | null = null;
  private fullValidation: FullRunValidationResult | null = null;
  private fullValidationRevision = -1;
  private fullValidationRunAt: string | null = null;
  private fullValidationPending = false;
  private fullValidationTimer: number | null = null;
  private fullValidationToken = 0;
  private replayElapsedSeconds = 0;
  private replayPaused = false;
  private rideEventCursor = 0;
  private audioSectionType: SemanticSectionType | null = null;
  private audioSectionId: string | undefined;
  private audioIntensityLevel: SimulatedIntensityLevel = 'comfortable';
  private simulationState: TrackBoundState = createTrackBoundState(0, 2);
  private simulationFrame: SimulationFrame | null = null;
  private mode: AppMode = 'menu';
  private status = 'Ready to build.';
  private debugVisible = location.hash === '#/debug';
  private persistenceAvailable = true;
  private editCommitTimer: number | null = null;
  private gestureOpen = false;
  private autosaveTimer: number | null = null;
  private saveSequence = 0;
  private saveQueue: Promise<void> = Promise.resolve();
  private drafts: StoredDraftSummary[] = [];
  private lastOpenedProjectId: string | null = null;
  private validationProfile: ValidationProfile = buildValidationProfile(
    systemsCircuitCoasterFixture,
    0,
  );
  private presetPreview: ProjectPreview | null = null;
  private autoBankPending = false;
  private autoBankToken = 0;
  private focusedLateralIssue: {
    readonly revision: number;
    readonly nodeIdsKey: string;
  } | null = null;
  private tutorialVisible = true;
  private tutorialStep: 1 | 2 | 3 = 1;
  private tutorialOperatingPieceEdited = false;
  private supportsVisible = true;
  private sceneryVisible = true;
  private snappingEnabled = true;
  private hoveredEditorTarget: { kind: 'node' | 'section'; id: string } | null = null;
  private rebuildMs = 0;
  private changedSpans = 0;
  private geometryRevision = 0;
  private peakSpeedMps = 0;
  private peakVerticalG = 1;
  private peakVerticalProgressPercent = 0;
  private minVerticalG = 1;
  private minVerticalProgressPercent = 0;
  private peakLateralG = 0;
  private peakLateralProgressPercent = 0;
  private peakIntensity: RuntimeIntensity = { ...INITIAL_INTENSITY };
  private peakIntensityScore = 0;
  private readonly visitedSectionTypes = new Set<SemanticSectionType>();
  private ridePhase: 'idle' | 'running' | 'completed' | 'stalled' | 'arrival_failed' = 'idle';
  private rideDistanceM = 0;
  private rideElapsedSeconds = 0;
  private lastRide: RuntimeRideResult | null = null;
  private accumulator = 0;
  private previousTime = performance.now();
  private animationFrame = 0;
  private pendingRebuild: number | null = null;
  private frameTimes: number[] = [];
  private inputLatencies: number[] = [];
  private pendingInputStartedAt: number | null = null;
  private pendingPreviewRefreshHandles = false;
  private rideCycles = 0;
  private diagnosticRideCompletionActive = false;
  private diagnosticRideCompletionToken = 0;
  private disposed = false;
  private testSpeed: TestSpeed = 1;

  private get project(): CoasterProject {
    return this.session.project;
  }

  private get selectedNodeId(): string | null {
    return this.session.selection.primaryNodeId;
  }

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.scene = new BunchaSceneService({
      canvas,
      qualityTier: qualityTier(),
      themeId: 'park_model',
    });
    this.cameraController = new RideCameraController(this.scene.camera);
    this.cameraController.setSettings({
      reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
    this.scene.camera.position.set(76, 58, 82);
    this.scene.camera.lookAt(0, 12, 0);
    this.controls = new OrbitControls(this.scene.camera, canvas);
    this.controls.target.set(0, 12, 0);
    this.controls.enableDamping = true;
    this.controls.minDistance = 25;
    this.controls.maxDistance = 180;
    this.controls.maxPolarAngle = Math.PI * 0.48;

    this.coasterMaterials = createCoasterMaterials({
      trackColor: asColor(this.project.appearance.trackColor),
      tieColor: 0x283744,
      supportColor: asColor(this.project.appearance.supportColor),
      trainColor: asColor(this.project.appearance.trainColor),
      stationColor: asColor(this.project.appearance.stationColor),
      stationRoofColor: 0x385768,
    });
    this.ghostMaterials = createCoasterMaterials({
      trackColor: 0x7ef5e0,
      tieColor: 0xc7fff5,
      supportColor: 0x58b9ac,
      trainColor: 0x7ef5e0,
      stationColor: 0xbafbef,
      stationRoofColor: 0x7ef5e0,
    });
    Object.values(this.ghostMaterials).forEach((material) => {
      material.transparent = true;
      material.opacity = 0.58;
      material.depthWrite = false;
      material.polygonOffset = true;
      material.polygonOffsetFactor = -2;
    });

    const initialCompiled = this.compileTrack([]);
    this.acceptCompiledTrack(initialCompiled);
    this.trackVisual = new TrackVisual(initialCompiled, this.coasterMaterials);
    this.trainVisual = new TrainVisual(this.coasterMaterials.train, this.project.train.carCount);
    this.authoredSceneryRoot.name = 'authored-scenery';
    this.validationRoot.name = 'validation-overlays';
    this.buildGuideRoot.name = 'build-guides';
    this.content.add(
      this.trackVisual.root,
      this.trainVisual.root,
      this.handleRoot,
      this.authoredSceneryRoot,
      this.validationRoot,
      this.buildGuideRoot,
      this.debugRoot,
    );
    this.scene.replaceContentRoot(this.content);
    this.rebuildHandles();
    this.rebuildBuildGuides();
    this.rebuildDebugFrames();
    this.placeTrain(initialTrainReferenceDistance(this.project, this.sampler ?? undefined));

    this.pointerEditor = new PointerEditor(canvas, this.scene.camera, this.handleRoot, {
      canEdit: () => this.mode === 'editor',
      getPickMode: () => {
        const editor = this.session.snapshot();
        if (editor.activeTool === 'scenery') return 'scenery';
        if (editor.activeTool !== 'build') return 'none';
        return editor.editorMode === 'advanced'
          ? 'both'
          : editor.selectionTarget === 'shape'
            ? 'node'
            : 'section';
      },
      getTrackRoot: () => this.trackVisual?.root ?? null,
      getSectionPickTargets: () => this.trackVisual?.sectionPickTargets() ?? [],
      getSceneryRoot: () => this.authoredSceneryRoot,
      onSelect: (nodeId, additive) => {
        const index = this.project.track.nodes.findIndex((node) => node.id === nodeId);
        this.selectNode(index, additive);
        this.beginEdit(true);
        this.controls.enabled = false;
      },
      onSelectSection: (sectionId) => {
        const index = this.project.track.sections.findIndex((section) => section.id === sectionId);
        if (index >= 0) this.selectSection(index);
      },
      onSelectScenery: (sceneryId) => {
        const index = this.project.scenery.findIndex((item) => item.id === sceneryId);
        if (index >= 0) this.selectScenery(index);
      },
      onHover: (target) => {
        if (
          this.hoveredEditorTarget?.kind === target?.kind &&
          this.hoveredEditorTarget?.id === target?.id
        )
          return;
        this.hoveredEditorTarget = target;
        this.refreshSelectionVisuals();
        if (!target) {
          const editor = this.session.snapshot();
          if (editor.activeTool !== 'build') {
            this.emit();
            return;
          }
          const selectionTarget = editor.selectionTarget;
          this.status =
            selectionTarget === 'shape'
              ? 'Shape Track: drag a cyan point to reshape the track.'
              : 'Tune Piece: click the rails to change its settings.';
          this.emit();
          return;
        }
        if (target.kind === 'node') {
          const index = this.project.track.nodes.findIndex((node) => node.id === target.id);
          if (index >= 0) this.status = `Shape Point ${index + 1} — drag to reshape.`;
        } else {
          const index = this.project.track.sections.findIndex(
            (section) => section.id === target.id,
          );
          const behavior = this.project.track.sections[index]?.behavior.kind;
          if (index >= 0)
            this.status = `Track Piece ${index + 1}${behavior ? ` (${behavior})` : ''} — click to tune.`;
        }
        this.emit();
      },
      onMarquee: (bounds) => {
        const world = new THREE.Vector3();
        const projected = new THREE.Vector3();
        const canvasBounds = this.canvas.getBoundingClientRect();
        const selectedIndices = this.handleRoot.children
          .map((handle) => {
            handle.getWorldPosition(world);
            projected.copy(world).project(this.scene.camera);
            const clientX = canvasBounds.left + ((projected.x + 1) / 2) * canvasBounds.width;
            const clientY = canvasBounds.top + ((1 - projected.y) / 2) * canvasBounds.height;
            const nodeId = String(handle.userData.nodeId);
            const index = this.project.track.nodes.findIndex((node) => node.id === nodeId);
            return { clientX, clientY, index };
          })
          .filter(
            ({ clientX, clientY, index }) =>
              index >= 0 &&
              clientX >= bounds.left &&
              clientX <= bounds.right &&
              clientY >= bounds.top &&
              clientY <= bounds.bottom,
          )
          .map(({ index }) => index)
          .sort((a, b) => a - b);
        if (!selectedIndices.length) return;
        const first = selectedIndices[0]!;
        const last = selectedIndices.at(-1)!;
        const nodeIds = this.project.track.nodes.slice(first, last + 1).map((node) => node.id);
        this.session.setSelection(nodeSelection(nodeIds));
        this.refreshSelectionVisuals();
        this.status = `${nodeIds.length} neighboring points selected.`;
        this.emit();
      },
      onMove: (nodeId, position) => {
        this.moveNode(nodeId, position);
      },
      onCommit: (nodeId, position) => {
        this.moveNode(nodeId, position);
        this.commitEdit();
      },
      onEnd: (changed) => {
        if (!changed) this.cancelEdit();
        this.controls.enabled = true;
      },
    });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas.parentElement ?? canvas);
    this.resize();
    this.setDebugVisible(this.debugVisible);
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.animationFrame = requestAnimationFrame(this.animate);
    void this.initializeRepository();
  }

  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): RuntimeSnapshot {
    const editor = this.session.snapshot();
    const validationPending = this.validationProfile.revision !== editor.revision;
    const validationProfile: ValidationProfile = validationPending
      ? {
          revision: editor.revision,
          issues: [],
          samples: [],
          blockerCount: 0,
          warningCount: 0,
          testAllowed: false,
          summary: 'Running Build Checks',
        }
      : this.validationProfile;
    const selected = this.selectedNode();
    const selectedWindingValues = this.project.track.nodes
      .filter((node) => editor.selection.nodeIds.includes(node.id))
      .map((node) => node.rollWindingDegrees);
    const selectedNodeIndex = selected ? this.project.track.nodes.indexOf(selected) : null;
    const selectedSectionIndex = editor.selection.sectionId
      ? this.project.track.sections.findIndex(
          (section) => section.id === editor.selection.sectionId,
        )
      : null;
    const selectedSection =
      selectedSectionIndex !== null && selectedSectionIndex >= 0
        ? this.project.track.sections[selectedSectionIndex]!
        : null;
    const firstBlocker = validationProfile.issues.find((issue) => issue.severity === 'blocker');
    const buildEndNode = editor.topology.status === 'open' ? this.project.track.nodes.at(-1) : null;
    const finishGuidance = buildEndNode ? measureFinishToStation(this.project) : null;
    const constructionState =
      editor.topology.status === 'invalid'
        ? ('invalid' as const)
        : this.project.track.nodes.length === 0 || editor.topology.status === 'incomplete'
          ? ('empty' as const)
          : editor.topology.status === 'open'
            ? finishGuidance?.generatedFinishEligible || finishGuidance?.directJoinEligible
              ? ('open_finishable' as const)
              : ('open_building' as const)
            : validationPending
              ? ('closed_checking' as const)
              : validationProfile.blockerCount
                ? ('closed_blocked' as const)
                : validationProfile.warningCount
                  ? ('closed_warnings' as const)
                  : ('closed_ready' as const);
    const blankTutorial =
      this.project.metadata.tags.includes('started_blank') ||
      this.project.metadata.title === 'My New Coaster' ||
      this.project.metadata.title === 'Blank Plot';
    const tutorialStep = blankTutorial
      ? this.project.track.nodes.length === 0
        ? 1
        : this.project.track.closed
          ? 3
          : 2
      : this.tutorialStep;
    const plotBounds = projectPlotBounds(this.project);
    const fullValidationCurrent =
      this.fullValidationRevision === editor.revision ? this.fullValidation : null;
    const fullValidationRequired = editor.topology.status === 'closed';
    const fullTestBlocked = fullValidationCurrent?.testPolicy === 'blocked';
    const testAllowed =
      validationProfile.testAllowed &&
      (!fullValidationRequired || (!this.fullValidationPending && !fullTestBlocked));
    const fullValidationStatus = !fullValidationCurrent
      ? 'not_run'
      : fullValidationCurrent.technicalEligible
        ? fullValidationCurrent.issues.some(
            (issue) => issue.severity === 'warning' || issue.severity === 'severe',
          )
          ? 'warnings'
          : 'valid'
        : 'blocked';
    const cameraSettings = this.cameraController.snapshot;
    const audioSnapshot = this.audio.snapshot;
    const replayDuration = this.lastRideSummary?.durationSeconds ?? 0;
    return {
      mode: this.mode,
      ready: true,
      status: this.status,
      editorRevision: editor.revision,
      selectedNodeId: this.selectedNodeId,
      selectedNodeIds: editor.selection.nodeIds,
      selectedNodeLabel:
        selectedNodeIndex === null
          ? null
          : buildEndNode?.id === selected?.id
            ? 'Build End'
            : editor.editorMode === 'advanced'
              ? `Shape Handle ${selectedNodeIndex + 1}`
              : 'Shape Point',
      selectedNodeIndex,
      nodeIds: this.project.track.nodes.map((node) => node.id),
      nodeCount: this.project.track.nodes.length,
      selectedHeightM: selected?.position.y ?? null,
      selectedPosition: selected ? { ...selected.position } : null,
      selectedBankDegrees: selected?.bankDegrees ?? null,
      selectedRollWindingDegrees: selected?.rollWindingDegrees ?? null,
      selectedRollWindingMixed:
        selectedWindingValues.length > 1 &&
        selectedWindingValues.some((value) => Math.abs(value - selectedWindingValues[0]!) > 0.001),
      selectedSectionId: selectedSection?.id ?? null,
      selectedSectionIndex:
        selectedSectionIndex !== null && selectedSectionIndex >= 0 ? selectedSectionIndex : null,
      selectedSectionLabel:
        selectedSectionIndex !== null && selectedSectionIndex >= 0
          ? `Track Piece ${selectedSectionIndex + 1}`
          : null,
      selectedSectionBehavior: selectedSection ? structuredClone(selectedSection.behavior) : null,
      autoBankPending: this.autoBankPending,
      sectionKinds: this.project.track.sections.map((section) => section.behavior.kind),
      canUndo: editor.history.canUndo,
      canRedo: editor.history.canRedo,
      fixtureId: this.project.projectId,
      fixtureTitle: this.project.metadata.title,
      activeTool: editor.activeTool,
      editorMode: editor.editorMode,
      selectionTarget: editor.selectionTarget,
      snappingEnabled: this.snappingEnabled,
      activeOverlay: editor.activeOverlay,
      saveState: editor.saveState,
      topologyStatus: editor.topology.status,
      constructionState,
      buildEnd:
        buildEndNode && finishGuidance
          ? {
              nodeId: buildEndNode.id,
              position: { ...buildEndNode.position },
              guidance: finishGuidance,
            }
          : null,
      plotBounds,
      testAllowed,
      testDisabledReason: testAllowed
        ? null
        : validationPending
          ? 'Build Checks are running.'
          : this.fullValidationPending
            ? 'Running the full test simulation.'
            : fullTestBlocked
              ? (fullValidationCurrent?.issues.find((issue) => issue.severity === 'blocker')
                  ?.message ?? 'Fix the ride simulation error before testing.')
              : (firstBlocker?.message ?? 'Finish and connect the track before test riding.'),
      validation: validationProfile,
      validationPending,
      preview: this.presetPreview
        ? {
            kind: this.presetPreview.kind,
            presetId: this.presetPreview.presetId,
            label: this.presetPreview.label,
            acceptLabel: this.presetPreview.acceptLabel,
            severity: this.presetPreview.severity,
            canAccept: this.presetPreview.canAccept,
            inline: this.presetPreview.inline,
            issues: this.presetPreview.issues,
            affectedNodeIds: this.presetPreview.affectedNodeIds,
            affectedSectionIds: this.presetPreview.affectedSectionIds,
            minPlotMarginM: this.presetPreview.minPlotMarginM,
            finishImpact: this.presetPreview.finishImpact ?? null,
            generatedLengthM: this.presetPreview.generatedLengthM ?? null,
            addedNodeCount: this.presetPreview.addedNodeCount ?? null,
            autoBankComparison: this.presetPreview.autoBankComparison ?? null,
          }
        : null,
      appearance: {
        themeId: this.project.appearance.environmentThemeId as EnvironmentThemeId,
        trackColor: this.project.appearance.trackColor,
        supportColor: this.project.appearance.supportColor,
        trainColor: this.project.appearance.trainColor,
      },
      supportsVisible: this.supportsVisible,
      sceneryVisible: this.sceneryVisible,
      sceneryCount: this.project.scenery.length,
      selectedSceneryId: this.session.selection.primarySceneryId,
      selectedSceneryIndex: this.session.selection.primarySceneryId
        ? this.project.scenery.findIndex(
            (item) => item.id === this.session.selection.primarySceneryId,
          )
        : null,
      selectedSceneryRotationDegrees:
        this.project.scenery.find((item) => item.id === this.session.selection.primarySceneryId)
          ?.rotationDegrees.y ?? null,
      drafts: this.drafts,
      lastOpenedProjectId: this.lastOpenedProjectId,
      tutorial: {
        visible: this.tutorialVisible,
        step: tutorialStep,
        kind: blankTutorial ? 'blank' : 'template',
      },
      telemetry: this.telemetry(),
      ride: {
        phase: this.ridePhase,
        progressPercent: this.rideProgressPercent(),
        elapsedSeconds: this.rideElapsedSeconds,
        dispatchSecondsRemaining: this.dispatchSecondsRemaining(),
        peakVerticalG: this.peakVerticalG,
        peakVerticalProgressPercent: this.peakVerticalProgressPercent,
        minVerticalG: this.minVerticalG,
        minVerticalProgressPercent: this.minVerticalProgressPercent,
        peakLateralG: this.peakLateralG,
        peakLateralProgressPercent: this.peakLateralProgressPercent,
        intensity: this.peakIntensity,
        visitedSectionTypes: [...this.visitedSectionTypes],
      },
      lastRide: this.lastRide,
      fullValidation: {
        pending: this.fullValidationPending,
        status: fullValidationStatus,
        outcome: fullValidationCurrent?.metrics.outcome ?? 'not_run',
        testPolicy: fullValidationCurrent?.testPolicy ?? 'blocked',
        publishReady: fullValidationCurrent?.publishReady ?? false,
        technicalEligible: fullValidationCurrent?.technicalEligible ?? false,
        intensity: fullValidationCurrent
          ? {
              level: fullValidationCurrent.intensity.level,
              label: fullValidationCurrent.intensity.label,
              message: fullValidationCurrent.intensity.message,
              progressPercent:
                this.sampler?.length && fullValidationCurrent.intensity.strongestDistanceM >= 0
                  ? THREE.MathUtils.clamp(
                      (fullValidationCurrent.intensity.strongestDistanceM / this.sampler.length) *
                        100,
                      0,
                      100,
                    )
                  : 0,
            }
          : null,
        topSpeedMph:
          fullValidationCurrent === null
            ? null
            : fullValidationCurrent.metrics.topSpeedMps * MPH_PER_MPS,
        maxVerticalG: fullValidationCurrent?.metrics.maxVerticalG ?? null,
        minVerticalG: fullValidationCurrent?.metrics.minVerticalG ?? null,
        maxAbsLateralG: fullValidationCurrent?.metrics.maxAbsLateralG ?? null,
        maxAbsLongitudinalG: fullValidationCurrent?.metrics.maxAbsLongitudinalG ?? null,
        issues:
          fullValidationCurrent?.issues.map((issue) => ({
            id: issue.id,
            code: issue.code,
            category: issue.category,
            ...(issue.intensityTier ? { intensityTier: issue.intensityTier } : {}),
            severity: issue.severity,
            message: issue.message,
            ...(issue.sectionId ? { sectionId: issue.sectionId } : {}),
            ...(issue.distanceM !== undefined ? { distanceM: issue.distanceM } : {}),
            ...(issue.measured !== undefined ? { measured: issue.measured } : {}),
            ...(issue.threshold !== undefined ? { threshold: issue.threshold } : {}),
            ...(issue.unit ? { unit: issue.unit } : {}),
          })) ?? [],
      },
      replay: {
        available: Boolean(this.lastRideSummary?.trace.length),
        paused: this.replayPaused,
        elapsedSeconds: this.replayElapsedSeconds,
        durationSeconds: replayDuration,
        progressPercent:
          replayDuration > 0
            ? THREE.MathUtils.clamp((this.replayElapsedSeconds / replayDuration) * 100, 0, 100)
            : 0,
      },
      settings: {
        cameraFov: cameraSettings.fieldOfView,
        cameraShake: cameraSettings.shake,
        reducedMotion: cameraSettings.reducedMotion,
        masterVolume: audioSnapshot.settings.masterVolume,
        muted: audioSnapshot.settings.muted,
        audioState: audioSnapshot.state,
        testSpeed: this.testSpeed,
      },
      diagnostics: this.diagnostics(),
      debugVisible: this.debugVisible,
      persistenceAvailable: this.persistenceAvailable,
      persistenceTier: this.repository.durability,
    };
  }

  openEditor(): void {
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = true;
    this.buildGuideRoot.visible = true;
    this.validationRoot.visible = true;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setSceneCameraFov(EDITOR_CAMERA_FOV);
    this.scene.camera.position.set(76, 58, 82);
    this.controls.target.set(0, 12, 0);
    this.controls.update();
    this.rebuildBuildGuides();
    this.selectNodeById(
      this.project.track.nodes[Math.min(2, this.project.track.nodes.length - 1)]?.id ?? null,
    );
    this.status = 'Pick a point, shape the track, then test ride it.';
    this.emit();
  }

  returnToMenu(): void {
    if (this.mode !== 'editor') return;
    this.commitEdit();
    this.cancelPreview();
    void this.flushAutosave();
    this.mode = transitionMode(this.mode, 'menu');
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.controls.enabled = true;
    this.status = 'Choose a coaster to keep building.';
    this.emit();
  }

  startTemplate(template: 'systems' | 'lift-drop' | 'oval' | 'blank'): void {
    const fixture =
      template === 'systems'
        ? systemsCircuitCoasterFixture
        : template === 'lift-drop'
          ? liftDropCoasterFixture
          : template === 'oval'
            ? ovalCoasterFixture
            : blankCoasterFixture;
    const title =
      template === 'systems'
        ? 'My First Coaster'
        : template === 'blank'
          ? 'My New Coaster'
          : fixture.metadata.title;
    const project = instantiateTemplate(fixture, title);
    if (template === 'blank' && !project.metadata.tags.includes('started_blank'))
      project.metadata.tags.push('started_blank');
    this.resetProject = cloneProject(project);
    this.replaceProject(project);
    this.session.setSelectionTarget('shape');
    if (template === 'blank') {
      this.session.setEditorMode('basic');
      this.session.setTool('build');
      this.tutorialStep = 1;
      this.rebuildHandles();
    } else if (template === 'systems') {
      this.tutorialStep = 1;
      this.tutorialOperatingPieceEdited = false;
      this.tutorialVisible = true;
    }
    this.lastOpenedProjectId = project.projectId;
    this.mode = 'menu';
    this.openEditor();
    this.status =
      template === 'blank'
        ? 'Add a station to start your track.'
        : 'Pick a point or section to start building.';
    this.scheduleAutosave();
    void this.repository.setPreference('lastOpenedProjectId', project.projectId);
    this.emit();
  }

  async openDraft(projectId: string): Promise<void> {
    const result = await this.repository.load(projectId);
    if (!result.ok) {
      const recovery = await this.repository.loadRecovery(projectId);
      if (!recovery.ok) return this.showOperationFailure(result.error.message);
      this.replaceProject(recovery.value.project);
      this.status = 'Recovered the last valid version.';
    } else {
      this.replaceProject(result.value.project);
      this.status = 'Draft opened.';
    }
    this.lastOpenedProjectId = projectId;
    this.resetProject = cloneProject(this.project);
    this.mode = 'menu';
    this.openEditor();
    void this.repository.setPreference('lastOpenedProjectId', projectId);
    this.emit();
  }

  async duplicateDraft(projectId: string): Promise<void> {
    const loaded = await this.repository.load(projectId);
    if (!loaded.ok) return this.showOperationFailure(loaded.error.message);
    const duplicate = instantiateTemplate(
      loaded.value.project,
      `${loaded.value.project.metadata.title} Copy`.slice(0, 80),
    );
    const saved = await this.repository.save(duplicate);
    if (!saved.ok) return this.showOperationFailure(saved.error.message);
    await this.refreshDrafts();
    this.status = 'Draft duplicated.';
    this.emit();
  }

  async deleteDraft(projectId: string): Promise<void> {
    const result = await this.repository.delete(projectId);
    if (!result.ok) return this.showOperationFailure(result.error.message);
    if (this.lastOpenedProjectId === projectId) this.lastOpenedProjectId = null;
    await this.refreshDrafts();
    this.status = 'Draft deleted.';
    this.emit();
  }

  renameProject(title: string): void {
    const result = renameProjectOperation(this.project, title);
    if (result.ok) this.executeProjectOperation('Rename coaster', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  async saveNow(): Promise<void> {
    await this.flushAutosave();
  }

  startRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'editor') return;
    this.commitEdit();
    if (!this.snapshot().testAllowed) {
      this.status = this.snapshot().testDisabledReason ?? 'Finish the track before test riding.';
      this.emit();
      return;
    }
    void this.audio.unlock();
    void this.flushAutosave();
    if (
      this.tutorialStep === 2 &&
      this.tutorialOperatingPieceEdited &&
      !this.project.metadata.tags.includes('started_blank')
    ) {
      this.tutorialStep = 3;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
    this.beginRide(camera);
  }

  restartRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.beginRide(camera);
  }

  private beginRide(camera: 'first-person' | 'third-person'): void {
    if (!this.sampler) return;
    this.releaseRideSession(true);
    this.mode = transitionMode(this.mode, 'test');
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.validationRoot.visible = false;
    this.controls.enabled = false;
    this.simulationState = createTrackBoundState(
      initialTrainReferenceDistance(this.project, this.sampler),
      0,
    );
    this.rideRunner = new RideRunner({
      sampler: this.sampler,
      family: coasterFamilyForProject(this.project),
      initialDistance: this.simulationState.sRef,
      maxDurationSeconds: 180,
    });
    this.rideEventCursor = 0;
    this.audioSectionType = null;
    this.audioSectionId = undefined;
    this.audioIntensityLevel = 'comfortable';
    this.replayElapsedSeconds = 0;
    this.replayPaused = false;
    this.simulationFrame = null;
    this.lastRideSummary = null;
    this.peakSpeedMps = 0;
    this.peakVerticalG = 1;
    this.peakVerticalProgressPercent = 0;
    this.minVerticalG = 1;
    this.minVerticalProgressPercent = 0;
    this.peakLateralG = 0;
    this.peakLateralProgressPercent = 0;
    this.peakIntensity = { ...INITIAL_INTENSITY };
    this.peakIntensityScore = 0;
    this.visitedSectionTypes.clear();
    this.ridePhase = 'running';
    this.rideCycles += 1;
    this.rideDistanceM = 0;
    this.rideElapsedSeconds = 0;
    this.accumulator = 0;
    this.cameraController.snapNextFrame();
    this.audio.emit({ type: 'station_hold' });
    this.status =
      this.fullValidation?.testPolicy === 'diagnostic_only'
        ? 'Diagnostic ride: this track has a technical issue to fix.'
        : 'Ride in progress.';
    this.emit();
  }

  setRideCamera(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.cameraController.snapNextFrame();
    this.emit();
  }

  startReplay(): void {
    if (
      (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') ||
      !this.lastRideSummary?.trace.length
    )
      return;
    this.mode = transitionMode(this.mode, 'replay');
    this.replayElapsedSeconds = 0;
    this.replayPaused = false;
    this.cameraController.snapNextFrame();
    this.audio.emit({ type: 'ui_confirm' });
    this.status = 'Cinematic replay.';
    this.emit();
  }

  toggleReplayPaused(): void {
    if (this.mode !== 'replay') return;
    this.replayPaused = !this.replayPaused;
    this.emit();
  }

  setCameraFov(value: number): void {
    this.cameraController.setSettings({ fieldOfView: value });
    this.persistRideSettings();
    this.emit();
  }

  setCameraShake(value: number): void {
    this.cameraController.setSettings({ shake: value });
    this.persistRideSettings();
    this.emit();
  }

  setReducedMotion(value: boolean): void {
    this.cameraController.setSettings({ reducedMotion: value });
    this.persistRideSettings();
    this.emit();
  }

  setMasterVolume(value: number): void {
    this.audio.setSettings({ masterVolume: value });
    this.persistRideSettings();
    this.emit();
  }

  setMuted(value: boolean): void {
    this.audio.setSettings({ muted: value });
    this.persistRideSettings();
    this.emit();
  }

  setTestSpeed(value: TestSpeed): void {
    if (![0.5, 1, 2, 4].includes(value)) return;
    this.testSpeed = value;
    this.persistRideSettings();
    this.emit();
  }

  returnToEditor(): void {
    if (
      this.mode !== 'ride-first-person' &&
      this.mode !== 'ride-third-person' &&
      this.mode !== 'replay' &&
      this.mode !== 'test'
    )
      return;
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = true;
    this.buildGuideRoot.visible = true;
    this.validationRoot.visible = true;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setSceneCameraFov(EDITOR_CAMERA_FOV);
    this.scene.camera.position.set(76, 58, 82);
    this.controls.target.set(0, 12, 0);
    this.controls.update();
    this.rideRunner?.stop();
    this.audio.update({ speedMetersPerSecond: 0, sectionType: null, running: false });
    this.rebuildBuildGuides();
    if (!this.lastRide || this.ridePhase === 'running') this.finishRideResult('ended');
    const resultLabel =
      this.lastRide?.outcome === 'completed'
        ? 'Track complete.'
        : this.lastRide?.outcome === 'stalled'
          ? 'Ride stalled.'
          : 'Test ended.';
    this.ridePhase = 'idle';
    this.status = `${resultLabel} Top speed: ${(this.peakSpeedMps * MPH_PER_MPS).toFixed(1)} mph.`;
    const completedRunner = this.rideRunner;
    this.emit();
    requestAnimationFrame(() => {
      if (this.disposed || this.mode !== 'editor' || this.rideRunner !== completedRunner) return;
      this.releaseRideSession(true);
      this.emit();
    });
  }

  private releaseRideSession(clearSummary: boolean): void {
    this.diagnosticRideCompletionToken += 1;
    this.diagnosticRideCompletionActive = false;
    this.rideRunner?.stop();
    this.rideRunner = null;
    this.simulationFrame = null;
    this.simulationState = createTrackBoundState(0, 0);
    this.rideEventCursor = 0;
    this.audioSectionType = null;
    this.audioSectionId = undefined;
    this.visitedSectionTypes.clear();
    this.accumulator = 0;
    if (clearSummary) {
      this.lastRideSummary = null;
      this.replayElapsedSeconds = 0;
      this.replayPaused = false;
    }
    this.audio.update({ speedMetersPerSecond: 0, sectionType: null, running: false });
  }

  resetFixture(): void {
    if (this.mode !== 'editor') return;
    this.cancelEdit();
    const reset = cloneProject(this.resetProject);
    reset.projectId = this.project.projectId;
    reset.revisionId = this.project.revisionId;
    reset.metadata = {
      ...reset.metadata,
      title: this.project.metadata.title,
      createdAt: this.project.metadata.createdAt,
      updatedAt: new Date().toISOString(),
      creator: this.project.metadata.creator,
    };
    this.session.execute(
      'Reset track',
      reset,
      nodeSelection([reset.track.nodes[2]?.id].filter(Boolean) as string[]),
    );
    this.rebuildProjectVisual();
    this.status = 'Track reset.';
    this.scheduleAutosave();
    this.emit();
  }

  loadDebugFixture(fixture: 'systems' | 'stall' | 'extreme'): void {
    if (
      !this.debugVisible ||
      this.mode === 'ride-first-person' ||
      this.mode === 'ride-third-person'
    )
      return;
    this.cancelEdit();
    this.activeFixture = fixture;
    this.replaceProject(cloneProject(fixtureFor(fixture)));
    this.selectNodeById(this.project.track.nodes[2]?.id ?? null);
    this.status =
      fixture === 'stall'
        ? 'Stall Test loaded.'
        : fixture === 'extreme'
          ? 'Extreme Warning Test loaded.'
          : 'Systems Circuit loaded.';
    this.emit();
  }

  completeRideForDiagnostics(): void {
    if (
      !this.debugVisible ||
      this.ridePhase !== 'running' ||
      !this.rideRunner ||
      this.diagnosticRideCompletionActive
    )
      return;
    const runner = this.rideRunner;
    const maximumSteps = Math.ceil(180 / FIXED_PHYSICS_STEP) + 1;
    const token = ++this.diagnosticRideCompletionToken;
    let completedSteps = 0;
    this.diagnosticRideCompletionActive = true;
    const runBatch = () => {
      if (
        this.disposed ||
        token !== this.diagnosticRideCompletionToken ||
        runner !== this.rideRunner ||
        this.ridePhase !== 'running'
      ) {
        if (token === this.diagnosticRideCompletionToken)
          this.diagnosticRideCompletionActive = false;
        return;
      }
      const batchEnd = Math.min(maximumSteps, completedSteps + 480);
      while (completedSteps < batchEnd && runner.result === 'running') {
        this.simulationFrame = runner.step(FIXED_PHYSICS_STEP);
        this.simulationState = this.simulationFrame.referenceState;
        this.peakSpeedMps = Math.max(this.peakSpeedMps, this.simulationState.velocity);
        this.updateRideMetrics(this.simulationFrame);
        this.syncRideEvents();
        completedSteps += 1;
      }
      if (runner.result === 'running' && completedSteps < maximumSteps) {
        requestAnimationFrame(runBatch);
        return;
      }
      this.diagnosticRideCompletionActive = false;
      this.lastRideSummary = runner.summary();
      this.endRide(
        runner.result === 'completed'
          ? 'completed'
          : runner.result === 'arrival_failed'
            ? 'arrival_failed'
            : 'stalled',
      );
    };
    requestAnimationFrame(runBatch);
  }

  undo(): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.cancelEdit();
    if (!this.session.undo()) return;
    this.rebuildProjectVisual();
    this.status = 'Last change undone.';
    this.scheduleAutosave();
    this.emit();
  }

  redo(): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.cancelEdit();
    if (!this.session.redo()) return;
    this.rebuildProjectVisual();
    this.status = 'Change restored.';
    this.scheduleAutosave();
    this.emit();
  }

  selectNode(index: number, additive = false): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    const nodeId = this.project.track.nodes[index]?.id ?? null;
    if (!nodeId) return;
    const existing = this.session.selection.nodeIds;
    const ids = additive
      ? existing.includes(nodeId)
        ? existing.filter((id) => id !== nodeId)
        : [...existing, nodeId]
      : [nodeId];
    this.session.setSelection(
      nodeSelection(ids, ids.includes(nodeId) ? nodeId : (ids.at(-1) ?? null)),
    );
    this.refreshSelectionVisuals();
    this.emit();
  }

  selectSection(index: number): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.session.setSelection(sectionSelection(this.project.track.sections[index]?.id ?? null));
    this.refreshSelectionVisuals();
    this.emit();
  }

  beginEdit(continuous = false): void {
    if (this.mode !== 'editor') return;
    void continuous;
    if (this.gestureOpen) return;
    this.gestureOpen = true;
    this.session.beginGesture('Edit track');
  }

  commitEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    if (!this.gestureOpen) return;
    try {
      this.rebuildProjectVisual();
    } catch {
      this.session.cancelGesture();
      this.rebuildProjectVisual();
      this.gestureOpen = false;
      this.status = "That edit couldn't produce a continuous track.";
      this.emit();
      return;
    }
    this.session.commitGesture();
    this.gestureOpen = false;
    const completedFirstChallenge =
      this.tutorialStep === 1 && !this.project.metadata.tags.includes('started_blank');
    if (completedFirstChallenge) {
      this.tutorialStep = 2;
      this.tutorialOperatingPieceEdited = false;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
    this.status = completedFirstChallenge
      ? 'Track reshaped. Next: tune a track piece and take a test ride.'
      : 'Track updated.';
    this.scheduleAutosave();
    this.emit();
  }

  setElevation(value: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const selected = this.selectedNode();
    if (selected && Math.abs(selected.position.y - value) < 0.0001) return;
    this.markInteraction();
    this.beginEdit();
    const result = setNodeElevation(this.project, this.session.selection.nodeIds, value);
    if (result.ok) this.previewProject(result.project, true);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    this.editCommitTimer = window.setTimeout(() => this.commitEdit(), 220);
  }

  setBank(value: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const selected = this.selectedNode();
    if (selected && Math.abs(selected.bankDegrees - value) < 0.0001) return;
    this.markInteraction();
    this.beginEdit();
    const result = setNodeBank(this.project, this.session.selection.nodeIds, value);
    if (result.ok) this.previewProject(result.project, false);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    this.editCommitTimer = window.setTimeout(() => this.commitEdit(), 220);
  }

  previewRemoveWinding(): void {
    if (this.mode !== 'editor') return;
    const nodeIds = this.session.selection.nodeIds;
    const result = removeNodeRollWinding(this.project, nodeIds);
    if (!result.ok) return this.showOperationFailure(result.message);
    this.showProjectPreview({
      kind: 'remove_winding',
      presetId: null,
      label: 'Remove Inversion Winding',
      command: 'Remove inversion winding',
      acceptLabel: 'Remove Winding',
      project: result.project,
      message: result.message,
      severity: 'warning',
      canAccept: true,
      inline: false,
      issues: [
        {
          code: 'remove_inversion_winding',
          severity: 'warning',
          message:
            'This removes authored roll rotation from every selected point. Review the ghost track before applying.',
        },
      ],
      affectedNodeIds: nodeIds,
      affectedSectionIds: [],
      minPlotMarginM: null,
    });
  }

  setNodeCoordinate(axis: 'x' | 'y' | 'z', value: number): void {
    const selected = this.selectedNode();
    if (!selected || !Number.isFinite(value)) return;
    if (axis === 'y') {
      const target = Math.max(1, Math.min(65, value));
      if (Math.abs(selected.position.y - target) < 0.0001) return;
      const result = setNodeElevation(this.project, this.session.selection.nodeIds, target);
      if (result.ok)
        this.executeProjectOperation(
          'Set track height',
          result.project,
          result.message,
          `coordinate:y:${[...this.session.selection.nodeIds].sort().join('|')}`,
        );
      return;
    }
    const target = this.snappingEnabled ? Math.round(value) : value;
    if (Math.abs(selected.position[axis] - target) < 0.0001) return;
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds,
      {
        x: axis === 'x' ? target - selected.position.x : 0,
        y: 0,
        z: axis === 'z' ? target - selected.position.z : 0,
      },
      this.snappingEnabled,
    );
    if (result.ok)
      this.executeProjectOperation(
        `Set track ${axis.toUpperCase()}`,
        result.project,
        result.message,
        `coordinate:${axis}:${[...this.session.selection.nodeIds].sort().join('|')}`,
      );
  }

  setSnapping(enabled: boolean): void {
    this.snappingEnabled = enabled;
    this.status = enabled ? 'Snapping on.' : 'Snapping off.';
    this.emit();
  }

  traverseSelection(direction: -1 | 1): void {
    if (!this.project.track.nodes.length) return;
    const current = this.session.selection.primaryNodeId
      ? this.project.track.nodes.findIndex(
          (node) => node.id === this.session.selection.primaryNodeId,
        )
      : -1;
    const next =
      (current + direction + this.project.track.nodes.length) % this.project.track.nodes.length;
    this.selectNode(next);
  }

  focusSelection(): void {
    const selected = this.selectedNode();
    if (!selected) return;
    const target = new THREE.Vector3(selected.position.x, selected.position.y, selected.position.z);
    this.controls.target.copy(target);
    this.scene.camera.position.copy(target).add(new THREE.Vector3(24, 18, 24));
    this.scene.camera.lookAt(target);
    this.controls.update();
    this.status = `${this.snapshot().selectedNodeLabel ?? 'Track point'} framed.`;
    this.emit();
  }

  nudgeSelected(horizontal: number, forwardAmount: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const forward = new THREE.Vector3();
    this.scene.camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
    const offset = right.multiplyScalar(horizontal).addScaledVector(forward, forwardAmount);
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds,
      {
        x: offset.x,
        y: 0,
        z: offset.z,
      },
      this.snappingEnabled,
    );
    if (result.ok)
      this.executeProjectOperation('Move track points', result.project, result.message);
  }

  setSectionBehavior(kind: TrackBehavior['kind']): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId || this.mode !== 'editor') return;
    const current = this.project.track.sections.find((section) => section.id === sectionId);
    const behavior: TrackBehavior =
      kind === 'station'
        ? { kind, holdSeconds: 1.5, dispatchSpeedMps: 2.5 }
        : kind === 'lift'
          ? { kind, targetSpeedMps: 5 }
          : kind === 'launch'
            ? { kind, targetSpeedMps: 20, accelerationMps2: 7 }
            : kind === 'brake'
              ? { kind, targetSpeedMps: 6, decelerationMps2: 5 }
              : { kind };
    const result = setSectionBehaviorOperation(this.project, sectionId, behavior);
    if (result.ok) {
      this.executeProjectOperation('Change section type', result.project, result.message);
      if (
        current &&
        JSON.stringify(current.behavior) !== JSON.stringify(behavior) &&
        ['lift', 'launch', 'brake'].includes(behavior.kind)
      )
        this.markTutorialOperatingPieceEdit();
    }
  }

  setSectionParameter(parameter: string, value: number): void {
    const sectionId = this.session.selection.sectionId;
    const current = this.project.track.sections.find((section) => section.id === sectionId);
    if (!current || this.mode !== 'editor') return;
    const behavior = structuredClone(current.behavior) as TrackBehavior & Record<string, number>;
    if (!(parameter in behavior)) return;
    if (Math.abs((behavior[parameter] ?? 0) - value) < 0.0001) return;
    behavior[parameter] = value;
    const result = setSectionBehaviorOperation(this.project, current.id, behavior);
    if (result.ok) {
      this.executeProjectOperation(
        'Tune section',
        result.project,
        'Section settings updated.',
        `section:${current.id}:${parameter}`,
      );
      if (['lift', 'launch', 'brake'].includes(behavior.kind))
        this.markTutorialOperatingPieceEdit();
    }
  }

  insertSelectedSection(): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId) {
      this.status = 'Select a track section first.';
      this.emit();
      return;
    }
    const compiledSection = this.sampler?.table.sections.find(
      (section) => section.id === sectionId,
    );
    const sampled =
      compiledSection && this.sampler
        ? this.sampler.sampleByDistance(
            (compiledSection.startDistance + compiledSection.endDistance) * 0.5,
          ).position
        : undefined;
    const result = insertNodeAtSection(this.project, sectionId, sampled);
    if (result.ok)
      this.executeProjectOperation('Insert track point', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  deleteSelected(): void {
    const nodeId = this.session.selection.primaryNodeId;
    if (!nodeId) {
      this.status = 'Select a track point to delete.';
      this.emit();
      return;
    }
    const result = deleteNode(this.project, nodeId);
    if (result.ok) {
      const nextId =
        result.project.track.nodes[
          Math.max(0, this.project.track.nodes.findIndex((node) => node.id === nodeId) - 1)
        ]?.id;
      this.executeProjectOperation(
        'Delete track point',
        result.project,
        result.message,
        undefined,
        nodeSelection(nextId ? [nextId] : []),
      );
    } else this.showOperationFailure(result.message);
  }

  duplicateSelectedSection(): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId) return this.showOperationFailure('Select a track section first.');
    const result = duplicateSection(this.project, sectionId);
    if (result.ok)
      this.executeProjectOperation('Duplicate section', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  smoothSelected(): void {
    const result = smoothNodes(this.project, this.session.selection.nodeIds);
    if (result.ok)
      this.showProjectPreview({
        kind: 'reshape',
        presetId: null,
        label: 'Smooth Selection',
        command: 'Smooth selection',
        acceptLabel: 'Apply Smooth',
        project: result.project,
        message: result.message,
        severity: 'clear',
        canAccept: true,
        inline: false,
        issues: [],
        affectedNodeIds: this.session.selection.nodeIds,
        affectedSectionIds: [],
        minPlotMarginM: null,
      });
    else this.showOperationFailure(result.message);
  }

  autoBankSelected(): void {
    void this.evaluateAutoBankSelection();
  }

  private async evaluateAutoBankSelection(): Promise<void> {
    if (this.mode !== 'editor' || this.autoBankPending) return;
    const nodeIds = [...this.session.selection.nodeIds];
    if (!nodeIds.length) return this.showOperationFailure('Select track points to auto-bank.');
    const candidates = generateAutoBankCandidates(this.project, nodeIds);
    if (!candidates.length)
      return this.showOperationFailure('These points do not have an adjustable bank profile.');

    const revision = this.session.snapshot().revision;
    const nodeIdsKey = [...nodeIds].sort().join('|');
    const token = ++this.autoBankToken;
    const project = structuredClone(this.project);
    const intervals = this.selectedTrackIntervals(nodeIds);
    const focusedHotspot =
      this.focusedLateralIssue?.revision === revision &&
      this.focusedLateralIssue.nodeIdsKey === nodeIdsKey;
    this.autoBankPending = true;
    this.status = 'Analyzing safer banking…';
    this.emit();

    try {
      const baseline = await this.autoBankValidationCoordinator.validate(project);
      if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
      if (baseline.metrics.outcome !== 'completed' || !baseline.technicalEligible) {
        this.status = 'Complete the ride successfully before using Auto-bank.';
        return;
      }
      const baselineGlobal = baseline.metrics.maxAbsLateralG;
      const baselineHotspot = focusedHotspot
        ? baselineGlobal
        : peakLateralGInIntervals(baseline.summary, intervals);
      const evaluated: Array<{
        candidate: (typeof candidates)[number];
        result: FullRunValidationResult;
        hotspot: number;
      }> = [];

      for (const candidate of candidates) {
        const result = await this.autoBankValidationCoordinator.validate(candidate.project);
        if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
        const hotspot = focusedHotspot
          ? result.metrics.maxAbsLateralG
          : peakLateralGInIntervals(result.summary, intervals);
        if (
          result.metrics.outcome === 'completed' &&
          result.technicalEligible &&
          !result.issues.some(
            (issue) => issue.category === 'technical' && issue.severity === 'blocker',
          ) &&
          result.metrics.maxAbsLateralG <= baselineGlobal + AUTO_BANK_GLOBAL_TOLERANCE_G &&
          baselineHotspot - hotspot >= AUTO_BANK_MINIMUM_IMPROVEMENT_G
        )
          evaluated.push({ candidate, result, hotspot });
      }

      if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
      evaluated.sort(
        (a, b) =>
          a.hotspot - b.hotspot ||
          a.result.metrics.maxAbsLateralG - b.result.metrics.maxAbsLateralG ||
          a.candidate.totalBankChangeDegrees - b.candidate.totalBankChangeDegrees ||
          a.candidate.scale - b.candidate.scale,
      );
      const best = evaluated[0];
      if (!best) {
        this.status =
          'Auto-bank couldn’t safely improve this section. Try reshaping the turn or adjusting the bank manually.';
        return;
      }

      const selected = new Set(nodeIds);
      const beforeBanks = project.track.nodes
        .filter((node) => selected.has(node.id))
        .map((node) => Math.abs(node.bankDegrees));
      const afterBanks = best.candidate.project.track.nodes
        .filter((node) => selected.has(node.id))
        .map((node) => Math.abs(node.bankDegrees));
      this.showProjectPreview({
        kind: 'auto_bank',
        presetId: null,
        label: 'Auto-bank Improvement',
        command: 'Auto-bank selection',
        acceptLabel: 'Apply Auto-bank',
        project: best.candidate.project,
        message: `Auto-bank reduced the highlighted lateral force from ${baselineHotspot.toFixed(2)} G to ${best.hotspot.toFixed(2)} G.`,
        severity: 'clear',
        canAccept: true,
        inline: false,
        issues: [],
        affectedNodeIds: nodeIds,
        affectedSectionIds: [],
        minPlotMarginM: null,
        autoBankComparison: {
          hotspotBeforeG: baselineHotspot,
          hotspotAfterG: best.hotspot,
          globalBeforeG: baselineGlobal,
          globalAfterG: best.result.metrics.maxAbsLateralG,
          maximumBankBeforeDegrees: Math.max(0, ...beforeBanks),
          maximumBankAfterDegrees: Math.max(0, ...afterBanks),
        },
      });
    } catch (error) {
      if (this.autoBankRequestIsCurrent(token, revision, nodeIdsKey))
        this.status =
          error instanceof Error && error.message.includes('superseded')
            ? 'Auto-bank stopped because the track changed.'
            : 'Auto-bank analysis could not finish. Your track was not changed.';
    } finally {
      if (token === this.autoBankToken) {
        this.autoBankPending = false;
        this.emit();
      }
    }
  }

  private selectedTrackIntervals(nodeIds: readonly string[]): readonly {
    startDistance: number;
    endDistance: number;
  }[] {
    if (!this.compiledTrack) return [];
    const indices = new Set(
      nodeIds
        .map((id) => this.project.track.nodes.findIndex((node) => node.id === id))
        .filter((index) => index >= 0),
    );
    return this.compiledTrack.spans
      .filter((span) => span.controlPointIndices.some((index) => indices.has(index)))
      .map((span) => ({ startDistance: span.startDistance, endDistance: span.endDistance }));
  }

  private autoBankRequestIsCurrent(token: number, revision: number, nodeIdsKey: string): boolean {
    return (
      token === this.autoBankToken &&
      this.mode === 'editor' &&
      this.session.snapshot().revision === revision &&
      [...this.session.selection.nodeIds].sort().join('|') === nodeIdsKey
    );
  }

  private cancelAutoBankAnalysis(): void {
    if (!this.autoBankPending) return;
    this.autoBankToken += 1;
    this.autoBankPending = false;
    this.autoBankValidationCoordinator.cancel();
  }

  alignSelected(axis: 'x' | 'y' | 'z'): void {
    const result = alignNodes(this.project, this.session.selection.nodeIds, axis);
    if (result.ok) this.executeProjectOperation(`Align ${axis}`, result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  rotateSelected(degrees: number): void {
    const result = rotateNodes(this.project, this.session.selection.nodeIds, degrees);
    if (result.ok) this.executeProjectOperation('Rotate selection', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  setTool(tool: EditorTool): void {
    this.session.setTool(tool);
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    this.refreshSelectionVisuals();
    this.emit();
  }

  setEditorMode(mode: EditorMode): void {
    this.session.setEditorMode(mode);
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    this.rebuildHandles();
    this.refreshSelectionVisuals();
    this.emit();
  }

  setSelectionTarget(target: 'shape' | 'piece'): void {
    if (this.mode !== 'editor') return;
    this.session.setSelectionTarget(target);
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    if (target === 'shape' && this.session.selection.sectionId) {
      const fallback = this.project.track.nodes[Math.min(2, this.project.track.nodes.length - 1)];
      this.session.setSelection(nodeSelection(fallback ? [fallback.id] : []));
    } else if (target === 'piece' && this.session.selection.nodeIds.length) {
      this.session.setSelection(sectionSelection(null));
    }
    this.refreshSelectionVisuals();
    this.status =
      target === 'shape'
        ? 'Shape Track: drag a cyan point to reshape the track.'
        : 'Tune Piece: click the rails to change lift, launch, brakes, or station settings.';
    this.emit();
  }

  setOverlay(overlay: EditorOverlay): void {
    this.session.setOverlay(overlay);
    this.rebuildValidationOverlay();
    this.emit();
  }

  selectBuildPiece(preset: ElementPresetId): void {
    const candidate = buildPresetCandidate(this.project, preset, this.session.selection.sectionId);
    if ('ok' in candidate) {
      if (!candidate.ok) this.showOperationFailure(candidate.message);
      return;
    }
    const preflightIssues = candidate.preflight.issues.map((issue) => ({
      code: issue.code,
      severity: issue.severity,
      message: issue.message,
    }));
    const guidanceIssues = candidate.warnings.map((message, index) => ({
      code: `preset_guidance_${index + 1}`,
      severity: 'warning' as const,
      message,
    }));
    const impact = finishImpact(this.project, candidate.project);
    const losesFinishRange =
      impact?.finishEligibleBefore === true && impact.finishEligibleAfter === false;
    const materiallyWorsensFinish = Boolean(
      impact &&
      (impact.distanceAfterM >= impact.distanceBeforeM + 5 ||
        impact.headingAfterDegrees >= impact.headingBeforeDegrees + 20),
    );
    const finishIssues =
      losesFinishRange || materiallyWorsensFinish
        ? [
            {
              code: losesFinishRange ? 'finish_range_lost' : 'finish_route_worsened',
              severity: 'warning' as const,
              message: losesFinishRange
                ? `This piece moves the Build End outside Finish range (${impact!.distanceBeforeM.toFixed(0)} m → ${impact!.distanceAfterM.toFixed(0)} m).`
                : `This piece moves the Build End farther from home (${impact!.distanceBeforeM.toFixed(0)} m → ${impact!.distanceAfterM.toFixed(0)} m).`,
            },
          ]
        : [];
    const issues = [...preflightIssues, ...guidanceIssues, ...finishIssues];
    const severity = candidate.preflight.canAccept
      ? issues.length
        ? ('warning' as const)
        : ('clear' as const)
      : ('blocker' as const);
    const kind =
      preset === 'station'
        ? ('station' as const)
        : this.project.track.closed
          ? ('reshape' as const)
          : ('piece' as const);
    this.showProjectPreview({
      kind,
      presetId: candidate.preset.id,
      label: candidate.preset.label,
      command: `Add ${candidate.preset.label}`,
      acceptLabel:
        preset === 'station'
          ? 'Place Station'
          : this.project.track.closed
            ? `Apply ${candidate.preset.label}`
            : `Build ${candidate.preset.label}`,
      project: candidate.project,
      message: candidate.message,
      severity,
      canAccept: candidate.preflight.canAccept,
      inline: kind === 'piece',
      issues,
      affectedNodeIds: candidate.affectedNodeIds,
      affectedSectionIds: candidate.affectedSectionIds,
      minPlotMarginM: Number.isFinite(candidate.preflight.minPlotMarginM)
        ? candidate.preflight.minPlotMarginM
        : null,
      finishImpact: impact,
      ...(candidate.focusNodeId ? { focusNodeId: candidate.focusNodeId } : {}),
    });
  }

  acceptPreview(): void {
    if (!this.presetPreview?.canAccept) return;
    const preview = this.presetPreview;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.executeProjectOperation(
      preview.command,
      preview.project,
      preview.message,
      undefined,
      preview.focusNodeId ? nodeSelection([preview.focusNodeId]) : this.session.selection,
    );
    if (preview.focusNodeId && !this.project.track.closed) this.focusBuildEnd();
    if (preview.kind === 'finish' || preview.kind === 'direct_join') {
      this.tutorialVisible = false;
      this.emit();
    }
    if (
      preview.presetId &&
      this.tutorialStep === 1 &&
      !this.project.metadata.tags.includes('started_blank')
    ) {
      this.tutorialStep = 2;
      this.tutorialOperatingPieceEdited = false;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
  }

  cancelPreview(): void {
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.rebuildBuildGuides();
    this.status = 'Preview cancelled.';
    this.emit();
  }

  connectEndpoints(): void {
    const result = connectEndpoints(this.project);
    if (result.ok) {
      const beforeLength = compiledTrackLength(this.project);
      const afterLength = compiledTrackLength(result.project);
      const baseNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
      const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
      this.showProjectPreview({
        kind: 'direct_join',
        presetId: null,
        label: 'Direct Join to Station',
        command: 'Direct Join to Station',
        acceptLabel: 'Finish Track',
        project: result.project,
        message: result.message,
        severity: 'warning',
        canAccept: true,
        inline: false,
        issues: [
          {
            code: 'direct_join_guidance',
            severity: 'warning',
            message: 'Direct Join is a precision option. Check the highlighted closing span.',
          },
        ],
        affectedNodeIds: result.project.track.nodes
          .filter((node) => !baseNodeIds.has(node.id))
          .map((node) => node.id),
        affectedSectionIds: result.project.track.sections
          .filter((section) => !baseSectionIds.has(section.id))
          .map((section) => section.id),
        minPlotMarginM: null,
        generatedLengthM:
          beforeLength !== null && afterLength !== null
            ? Math.max(0, afterLength - beforeLength)
            : null,
        addedNodeCount: result.project.track.nodes.length - this.project.track.nodes.length,
      });
    } else this.showOperationFailure(result.message);
  }

  finishToStation(): void {
    const result = assessFinishToStation(this.project);
    if (result.ok) {
      const beforeLength = compiledTrackLength(this.project);
      const afterLength = compiledTrackLength(result.project);
      const baseNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
      const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
      this.showProjectPreview({
        kind: 'finish',
        presetId: null,
        label: 'Finish to Station',
        command: 'Finish to Station',
        acceptLabel: 'Finish Track',
        project: result.project,
        message: result.message,
        severity: 'warning',
        canAccept: true,
        inline: false,
        issues: [
          {
            code: `finish_${result.mode}`,
            severity: 'warning',
            message:
              result.mode === 'aligned'
                ? 'The final handle will align with the station approach.'
                : 'The highlighted finish becomes ordinary editable track after acceptance.',
          },
        ],
        affectedNodeIds: result.project.track.nodes
          .filter((node) => !baseNodeIds.has(node.id))
          .map((node) => node.id),
        affectedSectionIds: result.project.track.sections
          .filter((section) => !baseSectionIds.has(section.id))
          .map((section) => section.id),
        minPlotMarginM: null,
        generatedLengthM:
          beforeLength !== null && afterLength !== null
            ? Math.max(0, afterLength - beforeLength)
            : null,
        addedNodeCount: result.project.track.nodes.length - this.project.track.nodes.length,
      });
    } else this.showOperationFailure(result.message);
  }

  focusBuildEnd(): void {
    if (this.project.track.closed) return;
    const end = this.project.track.nodes.at(-1);
    if (!end) return;
    const nextTarget = new THREE.Vector3(end.position.x, end.position.y, end.position.z);
    const offset = this.scene.camera.position.clone().sub(this.controls.target);
    this.controls.target.copy(nextTarget);
    this.scene.camera.position.copy(nextTarget).add(offset);
    this.scene.camera.lookAt(nextTarget);
    this.controls.update();
    this.status = 'Build End framed.';
    this.emit();
  }

  setTheme(theme: EnvironmentThemeId): void {
    if (this.project.appearance.environmentThemeId === theme) return;
    const next = cloneProject(this.project);
    next.appearance.environmentThemeId = theme;
    next.metadata.updatedAt = new Date().toISOString();
    this.session.execute('Change environment', next);
    this.carryValidationForward();
    this.scene.setTheme(theme);
    this.status = `${theme === 'park_model' ? 'Park Model' : theme === 'toyworks' ? 'Toyworks' : 'Afterglow'} theme applied.`;
    this.scheduleAutosave();
    if (this.tutorialStep === 3 && !this.project.metadata.tags.includes('started_blank')) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.status += ' First Coaster challenge complete.';
    }
    this.emit();
  }

  setAppearanceColor(slot: 'track' | 'support' | 'train', value: string): void {
    if (!/^#[0-9a-fA-F]{6}$/.test(value)) return;
    const next = cloneProject(this.project);
    if (slot === 'track') next.appearance.trackColor = value;
    if (slot === 'support') next.appearance.supportColor = value;
    if (slot === 'train') next.appearance.trainColor = value;
    next.metadata.updatedAt = new Date().toISOString();
    this.session.execute(
      `Change ${slot} color`,
      next,
      this.session.selection,
      `appearance:${slot}`,
    );
    this.carryValidationForward();
    this.applyAppearance();
    this.scheduleAutosave();
    if (this.tutorialStep === 3 && !this.project.metadata.tags.includes('started_blank')) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.status = `${slot[0]!.toUpperCase()}${slot.slice(1)} color applied. First Coaster challenge complete.`;
    }
    this.emit();
  }

  setLayerVisible(layer: 'supports' | 'scenery', visible: boolean): void {
    if (layer === 'supports') {
      this.supportsVisible = visible;
      this.trackVisual?.root.traverse((object) => {
        if (object.name === 'track-supports') object.visible = visible;
      });
    } else {
      this.sceneryVisible = visible;
      this.authoredSceneryRoot.visible = visible;
    }
    this.emit();
  }

  addScenery(assetId: 'tree' | 'hedge' | 'lamp' | 'rock'): void {
    if (this.project.scenery.length >= 128)
      return this.showOperationFailure('This scene already has the maximum 128 scenery pieces.');
    const next = cloneProject(this.project);
    const index = next.scenery.length;
    const id = newStableId('scenery');
    next.scenery.push({
      id,
      assetId,
      position: { x: -36 + (index % 8) * 10, y: 0.5, z: 28 - Math.floor(index / 8) * 8 },
      rotationDegrees: { x: 0, y: (index * 37) % 360, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      colorVariant: null,
    });
    next.metadata.updatedAt = new Date().toISOString();
    const selection: EditorSelectionState = {
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [id],
      primarySceneryId: id,
    };
    this.executeProjectOperation(
      `Add ${assetId}`,
      next,
      `${assetId[0]!.toUpperCase()}${assetId.slice(1)} added.`,
      undefined,
      selection,
    );
  }

  selectScenery(index: number): void {
    if (this.mode !== 'editor') return;
    const id = this.project.scenery[index]?.id;
    if (!id) return;
    this.session.setSelection({
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [id],
      primarySceneryId: id,
    });
    this.refreshSelectionVisuals();
    this.emit();
  }

  nudgeSelectedScenery(dx: number, dz: number): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    const target = next.scenery.find((candidate) => candidate.id === id)!;
    target.position.x = THREE.MathUtils.clamp(target.position.x + dx, -62, 62);
    target.position.z = THREE.MathUtils.clamp(target.position.z + dz, -42, 42);
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation('Move scenery', next, 'Scenery moved.', `scenery:${id}:position`);
  }

  rotateSelectedScenery(degrees: number): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    const target = next.scenery.find((candidate) => candidate.id === id)!;
    target.rotationDegrees.y = (((target.rotationDegrees.y + degrees) % 360) + 360) % 360;
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Rotate scenery',
      next,
      'Scenery rotated.',
      `scenery:${id}:rotation`,
    );
  }

  duplicateSelectedScenery(): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    if (this.project.scenery.length >= 128)
      return this.showOperationFailure('This scene already has the maximum 128 scenery pieces.');
    const next = cloneProject(this.project);
    const duplicate = structuredClone(item);
    duplicate.id = newStableId('scenery');
    duplicate.position.x = THREE.MathUtils.clamp(duplicate.position.x + 3, -62, 62);
    duplicate.position.z = THREE.MathUtils.clamp(duplicate.position.z + 3, -42, 42);
    next.scenery.push(duplicate);
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation('Duplicate scenery', next, 'Scenery duplicated.', undefined, {
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [duplicate.id],
      primarySceneryId: duplicate.id,
    });
  }

  deleteSelectedScenery(): void {
    const selected = new Set(this.session.selection.sceneryIds);
    if (!selected.size) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    next.scenery = next.scenery.filter((item) => !selected.has(item.id));
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Delete scenery',
      next,
      'Scenery deleted.',
      undefined,
      sectionSelection(null),
    );
  }

  resetScene(): void {
    const next = cloneProject(this.project);
    next.scenery = [];
    next.appearance = {
      ...next.appearance,
      environmentThemeId: 'park_model',
      trackColor: '#2457b2',
      supportColor: '#343a40',
      trainColor: '#d94a38',
    };
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Reset scene',
      next,
      'Scene reset. Track work kept.',
      undefined,
      sectionSelection(null),
    );
  }

  frameHeroPreview(): void {
    const positions = this.project.track.nodes.map((node) => node.position);
    if (!positions.length) return;
    const minX = Math.min(...positions.map((position) => position.x));
    const maxX = Math.max(...positions.map((position) => position.x));
    const minZ = Math.min(...positions.map((position) => position.z));
    const maxZ = Math.max(...positions.map((position) => position.z));
    const maxY = Math.max(...positions.map((position) => position.y));
    const target = new THREE.Vector3((minX + maxX) / 2, maxY * 0.35, (minZ + maxZ) / 2);
    const size = Math.max(maxX - minX, maxZ - minZ, 40);
    this.controls.target.copy(target);
    this.scene.camera.position.set(
      target.x + size * 0.9,
      target.y + size * 0.72,
      target.z + size * 0.9,
    );
    this.scene.camera.lookAt(target);
    this.controls.update();
    const next = cloneProject(this.project);
    next.heroCamera.position = { ...this.scene.camera.position };
    next.heroCamera.target = { ...target };
    next.heroCamera.verticalFovDegrees = this.scene.camera.fov;
    this.session.execute('Frame hero preview', next);
    this.carryValidationForward();
    this.scheduleAutosave();
    this.status = 'Hero preview framed.';
    this.emit();
  }

  focusIssue(issueId: string): void {
    const fastIssue = this.validationProfile.issues.find((candidate) => candidate.id === issueId);
    const fullIssue = this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    const issue = fastIssue ?? fullIssue;
    if (!issue) return;
    let position: THREE.Vector3 | null = null;
    if (fastIssue?.nodeId) {
      const index = this.project.track.nodes.findIndex((node) => node.id === fastIssue.nodeId);
      const node = this.project.track.nodes[index];
      if (node) {
        this.selectNode(index);
        position = new THREE.Vector3(node.position.x, node.position.y, node.position.z);
      }
    } else if (issue.sectionId) {
      const index = this.project.track.sections.findIndex(
        (section) => section.id === issue.sectionId,
      );
      if (index >= 0) this.selectSection(index);
      const span = this.compiledTrack?.spans[index];
      if (span && this.sampler) {
        const samplePosition = this.sampler.sampleByDistance(
          (span.startDistance + span.endDistance) * 0.5,
        ).position;
        position = new THREE.Vector3(samplePosition.x, samplePosition.y, samplePosition.z);
      }
    } else if (issue.distanceM !== undefined && this.sampler) {
      const samplePosition = this.sampler.sampleByDistance(issue.distanceM).position;
      position = new THREE.Vector3(samplePosition.x, samplePosition.y, samplePosition.z);
    }
    if (position) {
      this.controls.target.copy(position);
      this.scene.camera.position.copy(position).add(new THREE.Vector3(24, 18, 24));
      this.scene.camera.lookAt(position);
      this.controls.update();
    }
    const measured = fastIssue?.metric?.value ?? fullIssue?.measured;
    const threshold = fastIssue?.metric?.threshold ?? fullIssue?.threshold;
    const unit = fastIssue?.metric?.unit ?? fullIssue?.unit ?? '';
    const measurement =
      measured !== undefined && threshold !== undefined
        ? ` Measured ${measured.toFixed(2)}${unit ? ` ${unit}` : ''}; review line ${threshold.toFixed(2)}${unit ? ` ${unit}` : ''}.`
        : '';
    this.status = `${issue.message}${measurement}`;
    this.emit();
  }

  editIssue(issueId: string): void {
    const issue =
      this.validationProfile.issues.find((candidate) => candidate.id === issueId) ??
      this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    if (!issue) return;
    this.focusIssue(issueId);
    this.setTool('build');
    const fullIssue = this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    const code = fullIssue?.code ?? issue.code;
    const sectionIndex = issue.sectionId
      ? this.project.track.sections.findIndex((section) => section.id === issue.sectionId)
      : -1;
    const shapeCorrection = ['positive_vertical_g', 'negative_vertical_g', 'lateral_g'].includes(
      code,
    );
    if (shapeCorrection && sectionIndex >= 0) {
      const nodeCount = this.project.track.nodes.length;
      const indices = [sectionIndex - 1, sectionIndex, sectionIndex + 1, sectionIndex + 2]
        .map((index) => (this.project.track.closed ? (index + nodeCount) % nodeCount : index))
        .filter(
          (index, position, values) =>
            index >= 0 && index < nodeCount && values.indexOf(index) === position,
        );
      const nodeIds = indices.map((index) => this.project.track.nodes[index]!.id);
      this.session.setEditorMode('advanced');
      this.session.setSelectionTarget('shape');
      this.session.setSelection(nodeSelection(nodeIds, nodeIds[Math.min(2, nodeIds.length - 1)]));
      this.focusedLateralIssue =
        code === 'lateral_g'
          ? {
              revision: this.session.snapshot().revision,
              nodeIdsKey: [...nodeIds].sort().join('|'),
            }
          : null;
      if (code === 'lateral_g') this.session.setOverlay('lateral_g');
      else this.session.setOverlay('vertical_g');
      this.refreshSelectionVisuals();
      this.status =
        code === 'lateral_g'
          ? `Bank or smooth the highlighted curve. ${issue.message}`
          : `Smooth or reshape the highlighted curve. ${issue.message}`;
    } else {
      let targetSectionIndex = sectionIndex;
      if (code === 'station_arrival_overspeed' || code === 'ride_arrival_failed') {
        let brakeIndex = -1;
        for (let index = this.project.track.sections.length - 1; index >= 0; index -= 1)
          if (this.project.track.sections[index]!.behavior.kind === 'brake') {
            brakeIndex = index;
            break;
          }
        if (brakeIndex >= 0) targetSectionIndex = brakeIndex;
      }
      this.session.setSelectionTarget(targetSectionIndex >= 0 ? 'piece' : 'shape');
      if (targetSectionIndex >= 0)
        this.session.setSelection(
          sectionSelection(this.project.track.sections[targetSectionIndex]!.id),
        );
      this.refreshSelectionVisuals();
      this.status =
        code === 'station_arrival_overspeed' || code === 'ride_arrival_failed'
          ? `Strengthen the highlighted brake before the station. ${issue.message}`
          : targetSectionIndex >= 0
            ? `Tune the highlighted track piece. ${issue.message}`
            : `Reshape the highlighted track point. ${issue.message}`;
    }
    this.emit();
  }

  setTutorialVisible(visible: boolean): void {
    this.tutorialVisible = visible;
    void this.repository.setPreference('tutorial', { visible, step: this.tutorialStep });
    this.emit();
  }

  advanceTutorial(): void {
    if (this.tutorialStep === 3) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.emit();
      return;
    }
    this.status =
      this.tutorialStep === 1
        ? 'Reshape a hill to complete this step.'
        : this.tutorialOperatingPieceEdited
          ? 'Take a Test Ride to complete this step.'
          : 'Tune a lift, launch, or brake before taking a Test Ride.';
    this.emit();
  }

  private markTutorialOperatingPieceEdit(): void {
    if (this.tutorialStep !== 2 || this.project.metadata.tags.includes('started_blank')) return;
    this.tutorialOperatingPieceEdited = true;
    this.status = 'Track piece tuned. Take a Test Ride to continue.';
    this.emit();
  }

  exportProject(): Blob {
    return new Blob([serializeCoasterProject(this.project)], { type: 'application/json' });
  }

  importProject(json: string): void {
    const loaded = loadCoasterProject(json);
    if (!loaded.ok) return this.showOperationFailure(loaded.error.message);
    const topology = validateTrackTopology(loaded.project);
    if (topology.status === 'invalid')
      return this.showOperationFailure(
        topology.issues[0]?.message ?? 'That file does not contain one ordered track.',
      );
    if (loaded.project.track.nodes.length >= 4) {
      try {
        compileProjectTrackResult(loaded.project, new TrackCompiler());
      } catch {
        return this.showOperationFailure('That file could not produce one continuous track.');
      }
    }
    this.replaceProject(loaded.project);
    this.status =
      loaded.migratedFrom === null ? 'Coaster imported.' : 'Coaster imported and upgraded.';
    this.scheduleAutosave();
    this.emit();
  }

  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    this.debugRoot.visible = visible;
    if (visible && location.hash !== '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}#/debug`);
    if (!visible && location.hash === '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}`);
    this.emit();
  }

  async captureEvidence(): Promise<Blob> {
    this.scene.render();
    const blob = await new Promise<Blob | null>((resolve) =>
      this.canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) throw new Error('The WebGL evidence frame could not be encoded.');
    return blob;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.lastRideSummary = null;
    cancelAnimationFrame(this.animationFrame);
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    if (this.autosaveTimer !== null) window.clearTimeout(this.autosaveTimer);
    if (this.fullValidationTimer !== null) window.clearTimeout(this.fullValidationTimer);
    this.validationCoordinator.cancel();
    this.fullValidationCoordinator.dispose();
    this.autoBankValidationCoordinator.dispose();
    this.audio.dispose();
    this.resizeObserver.disconnect();
    this.pointerEditor.dispose();
    this.controls.dispose();
    this.clearHandles();
    this.trackVisual?.dispose();
    this.clearPresetPreviewVisual();
    this.trainVisual.dispose();
    disposeMaterialSet(this.coasterMaterials);
    disposeMaterialSet(this.ghostMaterials);
    this.scene.dispose();
    this.listeners.clear();
  }

  private selectedNode(): TrackNode | null {
    return this.project.track.nodes.find((node) => node.id === this.selectedNodeId) ?? null;
  }

  private clearPresetPreviewVisual(): void {
    if (!this.presetPreviewVisual) return;
    this.content.remove(this.presetPreviewVisual.root);
    this.presetPreviewVisual.dispose();
    this.presetPreviewVisual = null;
  }

  private setGhostSeverity(severity: RuntimePreviewState['severity']): void {
    const color = severity === 'blocker' ? 0xf26c6c : severity === 'warning' ? 0xf1bd58 : 0x7ef5e0;
    Object.values(this.ghostMaterials).forEach((material) => {
      if ('color' in material) material.color.setHex(color);
      if ('emissive' in material)
        material.emissive.setHex(
          severity === 'blocker' ? 0x641f2c : severity === 'warning' ? 0x5d3f12 : 0x1f665d,
        );
    });
  }

  private clearBuildGuides(): void {
    while (this.buildGuideRoot.children.length) {
      const child = this.buildGuideRoot.children.pop();
      if (!(child instanceof THREE.Line || child instanceof THREE.LineLoop)) continue;
      child.geometry.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => material.dispose());
    }
  }

  private rebuildBuildGuides(): void {
    this.clearBuildGuides();
    if (this.mode !== 'editor' || this.project.track.closed || !this.project.track.nodes.length)
      return;
    const bounds = projectPlotBounds(this.project);
    const boundaryGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(bounds.minX, this.project.plot.groundElevationMeters + 0.18, bounds.minZ),
      new THREE.Vector3(bounds.maxX, this.project.plot.groundElevationMeters + 0.18, bounds.minZ),
      new THREE.Vector3(bounds.maxX, this.project.plot.groundElevationMeters + 0.18, bounds.maxZ),
      new THREE.Vector3(bounds.minX, this.project.plot.groundElevationMeters + 0.18, bounds.maxZ),
    ]);
    const boundary = new THREE.LineLoop(
      boundaryGeometry,
      new THREE.LineBasicMaterial({
        color: this.presetPreview?.severity === 'blocker' ? 0xf26c6c : 0x75d7ce,
        transparent: true,
        opacity: this.presetPreview ? 0.88 : 0.42,
        depthTest: false,
      }),
    );
    boundary.name = 'build-plot-boundary';
    boundary.renderOrder = 80;
    this.buildGuideRoot.add(boundary);

    const first = this.project.track.nodes[0]!;
    const last = this.project.track.nodes.at(-1)!;
    const guide = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(last.position.x, last.position.y + 0.4, last.position.z),
        new THREE.Vector3(first.position.x, first.position.y + 0.4, first.position.z),
      ]),
      new THREE.LineDashedMaterial({
        color: 0xf1bd58,
        dashSize: 1.2,
        gapSize: 0.8,
        transparent: true,
        opacity: 0.72,
        depthTest: false,
      }),
    );
    guide.computeLineDistances();
    guide.name = 'finish-direction-guide';
    guide.renderOrder = 79;
    this.buildGuideRoot.add(guide);
  }

  private showProjectPreview(preview: ProjectPreview): void {
    this.clearPresetPreviewVisual();
    this.presetPreview = preview;
    this.setGhostSeverity(preview.severity);
    try {
      const compiled = compileProjectTrackResult(preview.project, new TrackCompiler());
      this.presetPreviewVisual = new TrackVisual(compiled, this.ghostMaterials);
      this.presetPreviewVisual.root.name = 'project-ghost-preview';
      this.presetPreviewVisual.root.traverse((object) => {
        object.renderOrder = 70;
      });
      if (preview.kind === 'piece' && preview.affectedSectionIds.length) {
        const affected = new Set(preview.affectedSectionIds);
        this.presetPreviewVisual.root.children.forEach((child) => {
          child.visible = affected.has(String(child.userData.sectionId));
        });
      }
      this.content.add(this.presetPreviewVisual.root);
    } catch {
      this.presetPreview = null;
      return this.showOperationFailure('That preview could not produce one continuous track.');
    }
    this.rebuildBuildGuides();
    this.status = preview.canAccept
      ? `${preview.label} preview ready.`
      : (preview.issues.find((issue) => issue.severity === 'blocker')?.message ??
        `${preview.label} cannot be placed here.`);
    this.emit();
  }

  private selectNodeById(nodeId: string | null): void {
    this.session.setSelection(nodeSelection(nodeId ? [nodeId] : []));
    this.refreshSelectionVisuals();
    this.emit();
  }

  private refreshSelectionVisuals(): void {
    const selectedIds = new Set(this.session.selection.nodeIds);
    this.handleRoot.children.forEach((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const selected = selectedIds.has(String(child.userData.nodeId));
      const buildEnd = child.userData.isBuildEnd === true;
      const material = child.material as THREE.MeshStandardMaterial;
      material.color.setHex(selected ? 0xc8fff1 : buildEnd ? 0x7ef5e0 : 0x5bd5ee);
      material.emissive.setHex(selected || buildEnd ? 0x2b8f82 : 0x123b4a);
      child.scale.setScalar(buildEnd ? (selected ? 1.55 : 1.35) : selected ? 1.25 : 1);
    });
    const selectedSceneryIds = new Set(this.session.selection.sceneryIds);
    this.authoredSceneryRoot.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const selected = selectedSceneryIds.has(String(object.userData.sceneryId));
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        if (material instanceof THREE.MeshStandardMaterial)
          material.emissive.setHex(selected ? 0x214f45 : 0x000000);
      });
    });
    const editor = this.session.snapshot();
    const showPieceEmphasis =
      editor.activeTool === 'build' &&
      (editor.editorMode === 'advanced' || editor.selectionTarget === 'piece');
    this.trackVisual?.setSectionEmphasis(
      showPieceEmphasis ? editor.selection.sectionId : null,
      showPieceEmphasis && this.hoveredEditorTarget?.kind === 'section'
        ? this.hoveredEditorTarget.id
        : null,
    );
  }

  private cancelEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    if (this.gestureOpen) {
      this.session.cancelGesture();
      this.gestureOpen = false;
      this.rebuildProjectVisual();
    }
  }

  private replaceProject(project: CoasterProject): void {
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    if (this.pendingRebuild !== null) {
      cancelAnimationFrame(this.pendingRebuild);
      this.pendingRebuild = null;
    }
    const currentProject = cloneProject(project);
    this.session.replaceProject(currentProject);
    this.rebuildProjectVisual();
    this.lastValidProject = cloneProject(currentProject);
  }

  private moveNode(nodeId: string, position: THREE.Vector3): void {
    const node = this.project.track.nodes.find((candidate) => candidate.id === nodeId);
    if (!node) return;
    const bounds = projectPlotBounds(this.project);
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds.length ? this.session.selection.nodeIds : [nodeId],
      {
        x: THREE.MathUtils.clamp(position.x, bounds.minX, bounds.maxX) - node.position.x,
        y: 0,
        z: THREE.MathUtils.clamp(position.z, bounds.minZ, bounds.maxZ) - node.position.z,
      },
      this.snappingEnabled,
    );
    if (result.ok) this.previewProject(result.project);
  }

  private markInteraction(): void {
    this.pendingInputStartedAt = performance.now();
  }

  private previewProject(project: CoasterProject, refreshHandles = true): void {
    this.session.preview(project);
    this.pendingPreviewRefreshHandles ||= refreshHandles;
    if (refreshHandles) {
      this.rebuildHandles();
      this.refreshSelectionVisuals();
      this.rebuildBuildGuides();
    }
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    this.pendingRebuild = requestAnimationFrame(() => {
      this.pendingRebuild = null;
      const started = performance.now();
      try {
        this.rebuildProjectVisual(false, this.pendingPreviewRefreshHandles, false);
        this.pendingPreviewRefreshHandles = false;
        this.rebuildMs = performance.now() - started;
        this.lastValidProject = cloneProject(this.project);
        this.status = 'Track updated.';
      } catch {
        this.session.cancelGesture();
        this.gestureOpen = false;
        this.rebuildProjectVisual();
        this.status = "That edit couldn't produce a continuous track. Restored the last version.";
      }
      this.emit();
    });
  }

  private executeProjectOperation(
    label: string,
    project: CoasterProject,
    message: string,
    mergeKey?: string,
    selectionAfter: EditorSelectionState = this.session.selection,
  ): void {
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    if (this.presetPreview) {
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
    }
    const topology = validateTrackTopology(project);
    if (topology.status === 'invalid') {
      this.showOperationFailure(
        topology.issues[0]?.message ?? 'That change would break the track.',
      );
      return;
    }
    if (project.track.nodes.length >= 4) {
      try {
        const preflight = compileProjectTrackResult(project, new TrackCompiler());
        const visual = new TrackVisual(preflight, this.coasterMaterials);
        visual.dispose();
      } catch {
        this.showOperationFailure('That change could not produce one continuous track.');
        return;
      }
    }
    if (!this.session.execute(label, project, selectionAfter, mergeKey)) return;
    const rebuildStarted = performance.now();
    this.rebuildProjectVisual();
    this.rebuildMs = performance.now() - rebuildStarted;
    this.lastValidProject = cloneProject(project);
    this.status = message;
    this.scheduleAutosave();
    this.emit();
  }

  private showOperationFailure(message: string): void {
    this.status = message;
    this.emit();
  }

  private rebuildProjectVisual(
    runValidation = true,
    refreshHandles = true,
    refreshScenery = true,
  ): void {
    const topology = validateTrackTopology(this.project);
    if (topology.status === 'invalid')
      throw new Error(topology.issues[0]?.message ?? 'Invalid track');
    const previousVisual = this.trackVisual;
    if (this.project.track.nodes.length < 4) {
      if (previousVisual) {
        this.content.remove(previousVisual.root);
        previousVisual.dispose();
      }
      this.trackVisual = null;
      this.compiledTrack = null;
      this.sampler = null;
      this.trainVisual.root.visible = false;
      this.compiler.clear();
    } else {
      const compiled = this.compileTrack([]);
      if (previousVisual) {
        this.changedSpans = previousVisual.rebuild(compiled);
        this.trackVisual = previousVisual;
      } else {
        const nextVisual = new TrackVisual(compiled, this.coasterMaterials);
        this.trackVisual = nextVisual;
        this.content.add(nextVisual.root);
        this.changedSpans = compiled.spans.length;
      }
      this.acceptCompiledTrack(compiled);
      this.trainVisual.root.visible = true;
      this.placeTrain(initialTrainReferenceDistance(this.project, compiled.sampler));
      this.geometryRevision += 1;
    }
    this.applyAppearance();
    if (refreshHandles) this.rebuildHandles();
    this.refreshSelectionVisuals();
    if (refreshScenery) this.rebuildAuthoredScenery();
    this.rebuildDebugFrames();
    if (runValidation) this.scheduleValidation();
  }

  private applyAppearance(): void {
    this.coasterMaterials.track.color.set(this.project.appearance.trackColor);
    this.coasterMaterials.support.color.set(this.project.appearance.supportColor);
    this.coasterMaterials.train.color.set(this.project.appearance.trainColor);
    this.coasterMaterials.station.color.set(this.project.appearance.stationColor);
    const theme = this.project.appearance.environmentThemeId as EnvironmentThemeId;
    if (this.scene.theme.id !== theme) this.scene.setTheme(theme);
    this.setLayerVisible('supports', this.supportsVisible);
  }

  private rebuildAuthoredScenery(): void {
    while (this.authoredSceneryRoot.children.length) {
      const child = this.authoredSceneryRoot.children.pop();
      child?.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        if (Array.isArray(object.material))
          object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      });
    }
    const material = (color: number) =>
      new THREE.MeshStandardMaterial({ color, roughness: 0.82, envMapIntensity: 0.32 });
    for (const item of this.project.scenery) {
      const root = new THREE.Group();
      root.name = `scene-item:${item.id}`;
      root.userData.sceneryId = item.id;
      if (item.assetId === 'tree') {
        const trunk = new THREE.Mesh(
          new THREE.CylinderGeometry(0.28, 0.4, 3, 8),
          material(0x624737),
        );
        trunk.position.y = 1.5;
        const crown = new THREE.Mesh(new THREE.ConeGeometry(1.7, 4.5, 9), material(0x39775a));
        crown.position.y = 4.4;
        root.add(trunk, crown);
      } else if (item.assetId === 'hedge') {
        const hedge = new THREE.Mesh(new THREE.BoxGeometry(3.5, 1.8, 1.4), material(0x4e8a62));
        hedge.position.y = 0.9;
        root.add(hedge);
      } else if (item.assetId === 'lamp') {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.1, 0.16, 3.5, 8),
          material(0x56636c),
        );
        post.position.y = 1.75;
        const globe = new THREE.Mesh(
          new THREE.SphereGeometry(0.32, 10, 8),
          new THREE.MeshStandardMaterial({
            color: 0xffdc91,
            emissive: 0xffb64a,
            emissiveIntensity: 0.6,
          }),
        );
        globe.position.y = 3.65;
        root.add(post, globe);
      } else {
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(1.25), material(0x727b79));
        rock.position.y = 0.8;
        root.add(rock);
      }
      root.position.set(item.position.x, item.position.y, item.position.z);
      root.userData.sceneryId = item.id;
      root.rotation.set(
        THREE.MathUtils.degToRad(item.rotationDegrees.x),
        THREE.MathUtils.degToRad(item.rotationDegrees.y),
        THREE.MathUtils.degToRad(item.rotationDegrees.z),
      );
      root.scale.set(item.scale.x, item.scale.y, item.scale.z);
      root.traverse((object) => {
        object.userData.sceneryId = item.id;
        if (!(object instanceof THREE.Mesh)) return;
        object.castShadow = true;
        object.receiveShadow = true;
      });
      this.authoredSceneryRoot.add(root);
    }
    this.authoredSceneryRoot.visible = this.sceneryVisible;
    this.refreshSelectionVisuals();
  }

  private scheduleValidation(): void {
    const revision = this.session.snapshot().revision;
    const fullValidationToken = ++this.fullValidationToken;
    this.fullValidationCoordinator.cancel();
    if (this.fullValidationTimer !== null) window.clearTimeout(this.fullValidationTimer);
    this.fullValidation = null;
    this.fullValidationRevision = -1;
    this.fullValidationRunAt = null;
    this.fullValidationPending = false;
    void this.validationCoordinator
      .analyze(cloneProject(this.project), revision)
      .then((profile) => {
        if (!profile || this.session.snapshot().revision !== revision) return;
        this.validationProfile = profile;
        this.rebuildValidationOverlay();
        if (profile.testAllowed && this.project.track.closed) {
          this.fullValidationPending = true;
          this.emit();
          this.fullValidationTimer = window.setTimeout(() => {
            this.fullValidationTimer = null;
            void this.fullValidationCoordinator
              .validate(cloneProject(this.project))
              .then((result) => {
                if (this.fullValidationToken !== fullValidationToken) return;
                this.fullValidation = result;
                this.fullValidationRevision = this.session.snapshot().revision;
                this.fullValidationRunAt = new Date().toISOString();
                if (result.metrics.outcome === 'completed') {
                  const issues = this.validationProfile.issues.filter(
                    (issue) => issue.code !== 'stall',
                  );
                  const blockerCount = issues.filter(
                    (issue) => issue.severity === 'blocker',
                  ).length;
                  const warningCount = issues.filter(
                    (issue) => issue.severity === 'warning',
                  ).length;
                  this.validationProfile = {
                    ...this.validationProfile,
                    issues,
                    blockerCount,
                    warningCount,
                    testAllowed: blockerCount === 0,
                    summary: blockerCount
                      ? `${blockerCount} issue${blockerCount === 1 ? '' : 's'} must be fixed`
                      : warningCount
                        ? `Ride complete · ${warningCount} build warning${warningCount === 1 ? '' : 's'}`
                        : `Ride complete · ${result.intensity.label} intensity`,
                  };
                }
                this.fullValidationPending = false;
                this.emit();
              })
              .catch(() => {
                if (this.fullValidationToken !== fullValidationToken) return;
                this.fullValidationPending = false;
                this.emit();
              });
          }, 450);
        }
        this.emit();
      });
  }

  private carryValidationForward(): void {
    this.validationCoordinator.cancel();
    if (this.fullValidation) this.fullValidationRevision = this.session.snapshot().revision;
    this.validationProfile = {
      ...this.validationProfile,
      revision: this.session.snapshot().revision,
    };
  }

  private rebuildValidationOverlay(): void {
    while (this.validationRoot.children.length) {
      const child = this.validationRoot.children.pop();
      child?.traverse((object) => {
        if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
        object.geometry.dispose();
        if (Array.isArray(object.material))
          object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      });
    }
    const overlay = this.session.snapshot().activeOverlay;
    if (overlay === 'none' || !this.sampler || !this.validationProfile.samples.length) return;
    const points: THREE.Vector3[] = [];
    const colors: number[] = [];
    const values = this.validationProfile.samples.map((sample) => {
      if (overlay === 'speed') return sample.speedMps;
      if (overlay === 'vertical_g') return Math.abs(sample.verticalG);
      if (overlay === 'lateral_g') return Math.abs(sample.lateralG);
      if (overlay === 'curvature') return sample.curvature;
      return Number.isFinite(sample.clearanceM) ? Math.max(0, 6 - sample.clearanceM) : 0;
    });
    const maximum = Math.max(0.001, ...values);
    this.validationProfile.samples.forEach((sample, index) => {
      const trackSample = this.sampler!.sampleByDistance(sample.distanceM);
      points.push(
        new THREE.Vector3(
          trackSample.position.x,
          trackSample.position.y + 0.55,
          trackSample.position.z,
        ),
      );
      const ratio = Math.min(1, values[index]! / maximum);
      const color = new THREE.Color().setHSL((1 - ratio) * 0.34, 0.82, 0.56);
      colors.push(color.r, color.g, color.b);
    });
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const line = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({
        vertexColors: true,
        depthTest: false,
        transparent: true,
        opacity: 0.95,
      }),
    );
    line.renderOrder = 80;
    this.validationRoot.add(line);
  }

  private compileTrack(changedIndices: readonly number[]): CompiledProjectTrack {
    return compileProjectTrackResult(this.project, this.compiler, changedIndices);
  }

  private acceptCompiledTrack(compiled: CompiledProjectTrack): void {
    this.compiledTrack = compiled;
    this.sampler = compiled.sampler;
  }

  private disposeHandle(handle: THREE.Mesh): void {
    [...handle.children].forEach((child) => {
      if (!(child instanceof THREE.Sprite)) return;
      handle.remove(child);
      const material = child.material as THREE.SpriteMaterial;
      material.map?.dispose();
      material.dispose();
    });
    handle.geometry.dispose();
    const materials = Array.isArray(handle.material) ? handle.material : [handle.material];
    materials.forEach((material) => material.dispose());
  }

  private clearHandles(): void {
    [...this.handleRoot.children].forEach((child) => {
      this.handleRoot.remove(child);
      if (child instanceof THREE.Mesh) this.disposeHandle(child);
    });
  }

  private syncPointLabel(handle: THREE.Mesh, labelText: string | null, isBuildEnd: boolean): void {
    const currentText = String(handle.userData.pointLabelText ?? '');
    const desiredText = labelText ?? '';
    if (currentText === desiredText) return;
    [...handle.children].forEach((child) => {
      if (!(child instanceof THREE.Sprite)) return;
      handle.remove(child);
      const material = child.material as THREE.SpriteMaterial;
      material.map?.dispose();
      material.dispose();
    });
    if (labelText) handle.add(this.createPointLabel(labelText, isBuildEnd));
    handle.userData.pointLabelText = desiredText;
  }

  private rebuildHandles(): void {
    const existingById = new Map<string, THREE.Mesh>();
    this.handleRoot.children.forEach((child) => {
      if (child instanceof THREE.Mesh) existingById.set(String(child.userData.nodeId), child);
    });
    const advanced = this.session.snapshot().editorMode === 'advanced';
    const buildEndId = this.project.track.closed ? null : this.project.track.nodes.at(-1)?.id;
    const liveNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
    existingById.forEach((handle, nodeId) => {
      if (liveNodeIds.has(nodeId)) return;
      this.handleRoot.remove(handle);
      this.disposeHandle(handle);
      existingById.delete(nodeId);
    });
    for (const [index, node] of this.project.track.nodes.entries()) {
      const isBuildEnd = node.id === buildEndId;
      const previous = existingById.get(node.id);
      const handle =
        previous && previous.userData.isBuildEnd === isBuildEnd
          ? previous
          : new THREE.Mesh(
              new THREE.SphereGeometry(isBuildEnd ? 1.5 : 1.25, 16, 10),
              new THREE.MeshStandardMaterial({
                color: isBuildEnd ? 0x7ef5e0 : 0x5bd5ee,
                emissive: isBuildEnd ? 0x2b8f82 : 0x123b4a,
                roughness: 0.25,
              }),
            );
      if (previous && previous !== handle) {
        this.handleRoot.remove(previous);
        this.disposeHandle(previous);
      }
      handle.position.set(node.position.x, node.position.y, node.position.z);
      handle.userData.nodeId = node.id;
      handle.userData.nodeIndex = index;
      handle.userData.isBuildEnd = isBuildEnd;
      handle.castShadow = false;
      this.syncPointLabel(
        handle,
        advanced || isBuildEnd ? (isBuildEnd && !advanced ? 'END' : String(index + 1)) : null,
        isBuildEnd,
      );
      // Re-adding an existing child keeps the raycast order aligned with authoritative node order.
      this.handleRoot.add(handle);
    }
  }

  private createPointLabel(text: string, buildEnd: boolean): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = '#07131a';
      context.beginPath();
      context.arc(48, 48, 38, 0, Math.PI * 2);
      context.fill();
      context.lineWidth = 7;
      context.strokeStyle = buildEnd ? '#7ef5e0' : '#c8fff1';
      context.stroke();
      context.fillStyle = '#ffffff';
      context.font =
        text.length > 2 ? '800 27px system-ui, sans-serif' : '700 46px system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, 48, 50);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    label.position.set(0, 2.7, 0);
    label.scale.set(buildEnd ? 4.2 : 3.5, buildEnd ? 4.2 : 3.5, 1);
    label.renderOrder = 50;
    return label;
  }

  private rebuildDebugFrames(): void {
    while (this.debugRoot.children.length) {
      const child = this.debugRoot.children[0]!;
      this.debugRoot.remove(child);
      child.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
    }
    if (!this.sampler) return;
    const frameCount = 18;
    for (let index = 0; index < frameCount; index += 1) {
      const sample = this.sampler.sampleByDistance((index / frameCount) * this.sampler.length);
      const origin = new THREE.Vector3(sample.position.x, sample.position.y, sample.position.z);
      this.debugRoot.add(
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.up.x, sample.up.y, sample.up.z),
          origin,
          2.4,
          0x78e3cc,
          0.4,
          0.2,
        ),
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.right.x, sample.right.y, sample.right.z),
          origin,
          1.8,
          0xf2c868,
          0.35,
          0.18,
        ),
      );
    }
    this.debugRoot.visible = this.debugVisible;
  }

  private placeTrain(distance: number): void {
    if (!this.sampler) return;
    this.trainVisual?.place(this.sampler, distance, this.project.train.carSpacingMeters);
  }

  private setSceneCameraFov(fov: number): void {
    if (Math.abs(this.scene.camera.fov - fov) < 0.01) return;
    this.scene.camera.fov = fov;
    this.scene.camera.updateProjectionMatrix();
  }

  private rideProgressPercent(): number {
    if (this.ridePhase === 'completed') return 100;
    if (!this.sampler?.length) return 0;
    return THREE.MathUtils.clamp((this.rideDistanceM / this.sampler.length) * 100, 0, 100);
  }

  private dispatchSecondsRemaining(): number {
    const station = this.simulationState.sectionRuntime.station as
      { enteredAt?: unknown; released?: unknown } | undefined;
    if (station?.released !== false || typeof station.enteredAt !== 'number' || !this.sampler)
      return 0;
    const section = this.sampler.sampleByDistance(this.simulationState.sRef).section;
    if (section?.type !== 'station') return 0;
    const rawHold = section.parameters?.holdSeconds;
    const holdSeconds = typeof rawHold === 'number' && Number.isFinite(rawHold) ? rawHold : 0;
    return Math.max(0, station.enteredAt + holdSeconds - this.simulationState.time);
  }

  private finishRideResult(outcome: RuntimeRideResult['outcome']): void {
    const summary = this.lastRideSummary;
    const authoritativeIntensity = this.fullValidation?.intensity;
    const trackLength = this.sampler?.length ?? 0;
    const progressAt = (distance: number) =>
      trackLength > 0 ? THREE.MathUtils.clamp((distance / trackLength) * 100, 0, 100) : 0;
    this.lastRide = {
      outcome,
      progressPercent: this.rideProgressPercent(),
      elapsedSeconds: summary?.durationSeconds ?? this.rideElapsedSeconds,
      peakSpeedMph: (summary?.topSpeed.value ?? this.peakSpeedMps) * MPH_PER_MPS,
      peakVerticalG: summary?.maxVerticalG.value ?? this.peakVerticalG,
      peakVerticalProgressPercent: summary
        ? progressAt(summary.maxVerticalG.distance)
        : this.peakVerticalProgressPercent,
      minVerticalG: summary?.minVerticalG.value ?? this.minVerticalG,
      minVerticalProgressPercent: summary
        ? progressAt(summary.minVerticalG.distance)
        : this.minVerticalProgressPercent,
      peakLateralG: summary ? Math.abs(summary.maxAbsLateralG.value) : this.peakLateralG,
      peakLateralProgressPercent: summary
        ? progressAt(summary.maxAbsLateralG.distance)
        : this.peakLateralProgressPercent,
      intensity: authoritativeIntensity
        ? {
            level: authoritativeIntensity.level,
            label: authoritativeIntensity.label,
            message: authoritativeIntensity.message,
            progressPercent: progressAt(authoritativeIntensity.strongestDistanceM),
          }
        : this.peakIntensity,
      visitedSectionTypes: [...this.visitedSectionTypes],
    };
    if (summary) {
      this.peakSpeedMps = summary.topSpeed.value;
      this.peakVerticalG = summary.maxVerticalG.value;
      this.peakVerticalProgressPercent = progressAt(summary.maxVerticalG.distance);
      this.minVerticalG = summary.minVerticalG.value;
      this.minVerticalProgressPercent = progressAt(summary.minVerticalG.distance);
      this.peakLateralG = Math.abs(summary.maxAbsLateralG.value);
      this.peakLateralProgressPercent = progressAt(summary.maxAbsLateralG.distance);
      this.rideElapsedSeconds = summary.durationSeconds;
      this.peakIntensity = this.lastRide.intensity;
    }
  }

  private endRide(outcome: 'completed' | 'stalled' | 'arrival_failed'): void {
    if (this.ridePhase !== 'running') return;
    this.ridePhase = outcome;
    this.finishRideResult(outcome);
    this.status =
      outcome === 'completed'
        ? 'Track complete! Your train finished the circuit and reached the station.'
        : outcome === 'arrival_failed'
          ? 'Station arrival failed. Add stronger brakes before the station.'
          : 'Ride stalled. Lower the next hill or add more speed.';
    this.emit();
  }

  private updateRideMetrics(frame: SimulationFrame): void {
    const sampler = this.sampler;
    if (!sampler) return;
    const next = frame.referenceState;
    this.rideDistanceM = (this.rideRunner?.progressPercent ?? 0) * sampler.length * 0.01;
    this.rideElapsedSeconds = next.time;
    const activeSection = sampler.sampleByDistance(next.sRef).section?.type ?? null;
    if (activeSection) this.visitedSectionTypes.add(activeSection);
    for (const force of frame.forces) {
      const windowedForce = force.windows['200'] ?? force;
      const progressPercent = THREE.MathUtils.clamp(
        (force.distance / sampler.length) * 100,
        0,
        100,
      );
      if (Math.abs(windowedForce.vertical) > this.peakVerticalG) {
        this.peakVerticalG = Math.abs(windowedForce.vertical);
        this.peakVerticalProgressPercent = progressPercent;
      }
      if (windowedForce.vertical < this.minVerticalG) {
        this.minVerticalG = windowedForce.vertical;
        this.minVerticalProgressPercent = progressPercent;
      }
      if (Math.abs(windowedForce.lateral) > this.peakLateralG) {
        this.peakLateralG = Math.abs(windowedForce.lateral);
        this.peakLateralProgressPercent = progressPercent;
      }
      const intensityScore = simulatedIntensityScore(windowedForce);
      if (intensityScore > this.peakIntensityScore) {
        const intensity: SimulatedIntensitySummary = classifySimulatedIntensity(windowedForce);
        this.peakIntensityScore = intensityScore;
        this.peakIntensity = { ...intensity, progressPercent };
        const ranks: Record<SimulatedIntensityLevel, number> = {
          comfortable: 0,
          noticeable: 1,
          intense: 2,
          extreme: 3,
          absurd: 4,
        };
        if (ranks[intensity.level] > ranks[this.audioIntensityLevel]) {
          this.audioIntensityLevel = intensity.level;
          if (intensity.level === 'noticeable') this.audio.emit({ type: 'intensity_warning' });
          else if (intensity.level === 'intense') this.audio.emit({ type: 'intensity_severe' });
          else if (intensity.level === 'extreme' || intensity.level === 'absurd')
            this.audio.emit({ type: 'intensity_block' });
        }
      }
    }
  }

  private syncRideEvents(): void {
    const runner = this.rideRunner;
    if (!runner) return;
    for (const event of runner.eventsSince(this.rideEventCursor)) {
      if (event.kind === 'station_dispatch') this.audio.emit({ type: 'station_dispatch' });
      else if (event.kind === 'photo')
        this.audio.emit({ type: 'photo_marker', sectionId: event.sectionId });
      else if (event.kind === 'stall') this.audio.emit({ type: 'stall' });
      else if (event.kind === 'complete') {
        this.audio.emit({ type: 'station_arrival' });
        this.audio.emit({ type: 'ride_complete' });
      } else if (event.kind === 'section_enter') {
        if (this.audioSectionType === 'lift')
          this.audio.emit({ type: 'lift_exit', sectionId: this.audioSectionId });
        else if (this.audioSectionType === 'launch')
          this.audio.emit({ type: 'launch_exit', sectionId: this.audioSectionId });
        else if (this.audioSectionType === 'brake')
          this.audio.emit({ type: 'brake_exit', sectionId: this.audioSectionId });
        this.audioSectionType = event.sectionType ?? null;
        this.audioSectionId = event.sectionId;
        if (event.sectionType === 'lift')
          this.audio.emit({ type: 'lift_enter', sectionId: event.sectionId });
        else if (event.sectionType === 'launch')
          this.audio.emit({ type: 'launch_enter', sectionId: event.sectionId });
        else if (event.sectionType === 'brake')
          this.audio.emit({ type: 'brake_enter', sectionId: event.sectionId });
      }
    }
    this.rideEventCursor = runner.eventCount;
  }

  private telemetry(): RuntimeTelemetry {
    const force = this.simulationFrame?.forces[0];
    const speed = this.simulationFrame?.referenceState.velocity ?? 0;
    const activeSectionType =
      this.simulationFrame && this.sampler
        ? (this.sampler.sampleByDistance(this.simulationFrame.referenceState.sRef).section?.type ??
          null)
        : null;
    return {
      speedMps: speed,
      speedMph: speed * MPH_PER_MPS,
      verticalG: force?.vertical ?? 1,
      lateralG: force?.lateral ?? 0,
      longitudinalG: force?.longitudinal ?? 0,
      peakSpeedMph: this.peakSpeedMps * MPH_PER_MPS,
      activeSectionType,
      activeSectionLabel: sectionLabel(activeSectionType),
    };
  }

  private diagnostics(): RuntimeDiagnostics {
    const renderer = this.scene.renderer;
    const info = renderer instanceof THREE.WebGLRenderer ? renderer.info : null;
    const continuity = this.trackVisual?.continuity() ?? {
      maxRailGapMeters: 0,
      maxRailNormalAngleDegrees: 0,
      maxTieSpacingMeters: 0,
    };
    return {
      qualityTier: this.scene.quality.tier,
      frameMsP50: percentile(this.frameTimes, 0.5),
      frameMsP95: percentile(this.frameTimes, 0.95),
      inputMsP50: percentile(this.inputLatencies, 0.5),
      inputMsP95: percentile(this.inputLatencies, 0.95),
      rebuildMs: this.rebuildMs,
      changedSpans: this.changedSpans,
      geometryRevision: this.geometryRevision,
      trackLengthM: this.sampler?.length ?? 0,
      sampleCount: this.sampler?.table.count ?? 0,
      rendererGeometries: info?.memory.geometries ?? 0,
      rendererTextures: info?.memory.textures ?? 0,
      rideCycles: this.rideCycles,
      retainedTraceSamples: this.lastRideSummary?.trace.length ?? 0,
      validationActive:
        this.fullValidationCoordinator.diagnostics.active +
        this.autoBankValidationCoordinator.diagnostics.active,
      validationQueued:
        this.fullValidationCoordinator.diagnostics.queued +
        this.autoBankValidationCoordinator.diagnostics.queued,
      audioActiveVoices: this.audio.snapshot.activeVoices,
      listenerCount: this.listeners.size,
      maxRailSeamGapM: continuity.maxRailGapMeters,
      maxRailSeamAngleDegrees: continuity.maxRailNormalAngleDegrees,
      maxTieSpacingM: continuity.maxTieSpacingMeters,
    };
  }

  private readonly animate = (time: number): void => {
    if (this.disposed) return;
    const frameMs = Math.min(100, time - this.previousTime);
    this.previousTime = time;
    this.frameTimes.push(frameMs);
    if (this.frameTimes.length > 600) this.frameTimes.shift();
    if (
      (this.mode === 'ride-first-person' || this.mode === 'ride-third-person') &&
      this.ridePhase === 'running' &&
      !this.diagnosticRideCompletionActive &&
      this.sampler
    ) {
      this.accumulator = Math.min(0.25, this.accumulator + (frameMs / 1000) * this.testSpeed);
      while (this.accumulator >= FIXED_PHYSICS_STEP) {
        if (!this.rideRunner) break;
        this.simulationFrame = this.rideRunner.step(FIXED_PHYSICS_STEP);
        this.simulationState = this.simulationFrame.referenceState;
        this.peakSpeedMps = Math.max(this.peakSpeedMps, this.simulationState.velocity);
        this.updateRideMetrics(this.simulationFrame);
        this.syncRideEvents();
        this.accumulator -= FIXED_PHYSICS_STEP;
        if (this.rideRunner.result !== 'running') {
          this.lastRideSummary = this.rideRunner.summary();
          this.endRide(
            this.rideRunner.result === 'completed'
              ? 'completed'
              : this.rideRunner.result === 'arrival_failed'
                ? 'arrival_failed'
                : 'stalled',
          );
        }
        if (this.ridePhase !== 'running') break;
      }
      if (this.simulationFrame) {
        this.trainVisual.placeFrame(this.simulationFrame);
        this.cameraController.updateRide(
          this.simulationFrame,
          this.sampler,
          this.mode === 'ride-first-person' ? 'first-person' : 'third-person',
        );
        const sectionType =
          this.sampler.sampleByDistance(this.simulationFrame.referenceState.sRef).section?.type ??
          null;
        this.audio.update({
          speedMetersPerSecond: this.simulationFrame.referenceState.velocity,
          sectionType,
          running: this.ridePhase === 'running',
        });
      }
    } else if (this.mode === 'replay' && this.sampler && this.lastRideSummary?.trace.length) {
      if (!this.replayPaused)
        this.replayElapsedSeconds = Math.min(
          this.lastRideSummary.durationSeconds,
          this.replayElapsedSeconds + (frameMs / 1000) * this.testSpeed,
        );
      const trace = this.lastRideSummary.trace;
      const traceIndex = Math.min(
        trace.length - 1,
        Math.floor(
          (this.replayElapsedSeconds / Math.max(0.001, this.lastRideSummary.durationSeconds)) *
            trace.length,
        ),
      );
      const sample = trace[traceIndex]!;
      this.placeTrain(sample.distance);
      this.cameraController.updateCinematic(
        this.sampler,
        sample.distance,
        this.replayElapsedSeconds,
      );
      this.audio.update({
        speedMetersPerSecond: sample.speed,
        sectionType: sample.sectionType,
        running: !this.replayPaused,
      });
      if (this.replayElapsedSeconds >= this.lastRideSummary.durationSeconds)
        this.replayPaused = true;
    } else {
      this.controls.update();
    }
    this.scene.render();
    if (this.pendingInputStartedAt !== null) {
      this.inputLatencies.push(Math.max(0, performance.now() - this.pendingInputStartedAt));
      if (this.inputLatencies.length > 120) this.inputLatencies.shift();
      this.pendingInputStartedAt = null;
    }
    if (Math.floor(time / 100) !== Math.floor((time - frameMs) / 100)) this.emit();
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  private resize(): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.scene.resize(bounds.width, bounds.height);
  }

  private async initializeRepository(): Promise<void> {
    const initialized = await this.repository.initialize();
    this.persistenceAvailable = initialized.ok;
    if (!initialized.ok) this.status = 'Saving is off. You can still build and ride.';
    const lastOpened = await this.repository.getPreference<string>('lastOpenedProjectId');
    if (lastOpened.ok) this.lastOpenedProjectId = lastOpened.value;
    const tutorial = await this.repository.getPreference<{ visible: boolean; step: 1 | 2 | 3 }>(
      'tutorial',
    );
    if (tutorial.ok && tutorial.value) {
      this.tutorialVisible = tutorial.value.visible;
      this.tutorialStep = tutorial.value.step;
    }
    const rideSettings = await this.repository.getPreference<{
      cameraFov: number;
      cameraShake: number;
      reducedMotion: boolean;
      masterVolume: number;
      muted: boolean;
      testSpeed?: TestSpeed;
    }>('rideSettings');
    if (rideSettings.ok && rideSettings.value) {
      this.cameraController.setSettings({
        fieldOfView: rideSettings.value.cameraFov,
        shake: rideSettings.value.cameraShake,
        reducedMotion: rideSettings.value.reducedMotion,
      });
      this.audio.setSettings({
        masterVolume: rideSettings.value.masterVolume,
        muted: rideSettings.value.muted,
      });
      if ([0.5, 1, 2, 4].includes(rideSettings.value.testSpeed ?? 1))
        this.testSpeed = rideSettings.value.testSpeed ?? 1;
    }
    await this.refreshDrafts();
    if (initialized.ok && initialized.value.importedLegacyDrafts)
      this.status = `${initialized.value.importedLegacyDrafts} earlier draft${initialized.value.importedLegacyDrafts === 1 ? '' : 's'} upgraded.`;
    this.emit();
  }

  private async refreshDrafts(): Promise<void> {
    const listed = await this.repository.list();
    if (listed.ok) this.drafts = listed.value;
    else if (listed.error.code === 'unavailable') this.persistenceAvailable = false;
  }

  private persistRideSettings(): void {
    const camera = this.cameraController.snapshot;
    const audio = this.audio.snapshot.settings;
    void this.repository.setPreference('rideSettings', {
      cameraFov: camera.fieldOfView,
      cameraShake: camera.shake,
      reducedMotion: camera.reducedMotion,
      masterVolume: audio.masterVolume,
      muted: audio.muted,
      testSpeed: this.testSpeed,
    });
  }

  private scheduleAutosave(): void {
    this.session.setSaveState('editing');
    if (this.autosaveTimer !== null) window.clearTimeout(this.autosaveTimer);
    this.autosaveTimer = window.setTimeout(() => {
      this.autosaveTimer = null;
      void this.flushAutosave();
    }, AUTOSAVE_DELAY_MS);
    this.emit();
  }

  private async flushAutosave(): Promise<void> {
    if (this.autosaveTimer !== null) {
      window.clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    const project = cloneProject(this.project);
    if (
      project.schemaVersion === 4 &&
      this.fullValidation &&
      this.fullValidationRevision === this.session.snapshot().revision
    )
      project.validation = toAuthoritativeValidationSummary(
        project,
        this.fullValidation,
        this.fullValidationRunAt ?? new Date().toISOString(),
      );
    const sequence = ++this.saveSequence;
    this.session.setSaveState('saving');
    this.emit();
    this.saveQueue = this.saveQueue.then(async () => {
      const saved = await this.repository.save(project);
      if (sequence !== this.saveSequence) return;
      this.persistenceAvailable = saved.ok || saved.error.code !== 'unavailable';
      if (!saved.ok) {
        this.session.setSaveState('error');
        this.status = saved.error.message;
        this.emit();
        return;
      }
      const topology = validateTrackTopology(project);
      if (topology.status !== 'invalid' && project.track.nodes.length >= 4) {
        try {
          compileProjectTrackResult(project, new TrackCompiler());
          await this.repository.saveRecovery(project);
        } catch {
          // Keep the previous last-valid checkpoint when compilation fails.
        }
      }
      this.lastOpenedProjectId = project.projectId;
      await this.repository.setPreference('lastOpenedProjectId', project.projectId);
      await this.refreshDrafts();
      this.session.setSaveState('saved');
      this.status = 'Saved on this device.';
      this.emit();
    });
    await this.saveQueue;
  }

  private emit(): void {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

export function createCoasterRuntime(canvas: HTMLCanvasElement): CoasterRuntime {
  return new ProductionCoasterRuntime(canvas);
}
